/* eslint-disable react/prop-types */
/**
 * ConformIMF - Gestion des Risques
 */
import { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import AuthContext from "../../context/AuthContext";
import { downloadCSV } from "../../utils/exportCSV";
import { fadeInUp, fadeInDown, fadeIn, stagger } from "../../utils/animations";
import ConformiteContent from "../conformite";

// ── Helpers ───────────────────────────────────────────────────────────────────

function computeScores(impact, prob, qc, eo) {
  const inherent = impact && prob ? impact * prob : null;
  const nc = qc && eo ? (qc + eo) / 2 : null;
  const residuel = inherent !== null && nc !== null ? +(inherent * (1 - nc / 5)).toFixed(2) : null;
  return { inherent, nc, residuel };
}

function getLevel(score, niveaux) {
  if (score === null || score === undefined || !niveaux.length) return null;
  const sorted = [...niveaux].sort((a, b) => a.score_min - b.score_min);
  const s = Math.max(1, Math.ceil(score));
  if (s >= sorted[sorted.length - 1].score_max) return sorted[sorted.length - 1];
  return sorted.find((n) => s >= n.score_min && s <= n.score_max) || sorted[0];
}

function ScoreChip({ score, niveaux }) {
  const lvl = getLevel(score, niveaux);
  if (!lvl) return null;
  return (
    <MDBox
      sx={{
        display: "inline-flex",
        alignItems: "center",
        bgcolor: lvl.couleur + "22",
        border: `1.5px solid ${lvl.couleur}`,
        borderRadius: "12px",
        px: 1.2,
        py: 0.3,
      }}
    >
      <MDBox
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: lvl.couleur,
          mr: 0.7,
          flexShrink: 0,
        }}
      />
      <MDTypography
        variant="caption"
        sx={{ color: lvl.couleur, fontWeight: 700, fontSize: "0.7rem" }}
      >
        {lvl.nom}
      </MDTypography>
    </MDBox>
  );
}

// Generate a gradient from green → yellow → red for N steps
function scaleColors(n) {
  const stops = ["#4CAF50", "#8BC34A", "#FFC107", "#FF9800", "#F44336"];
  if (n <= stops.length) return stops.slice(0, n);
  const result = [];
  for (let i = 0; i < n; i++) {
    result.push(stops[Math.round((i / (n - 1)) * (stops.length - 1))]);
  }
  return result;
}

function niveauxScale(niveaux, n) {
  const sorted = [...(niveaux || [])].sort((a, b) => a.ordre - b.ordre);
  const base = sorted.length ? sorted.map((nv) => nv.couleur) : scaleColors(5);
  const result = [];
  for (let i = 0; i < n; i++) {
    const idx = Math.round((i / Math.max(n - 1, 1)) * (base.length - 1));
    result.push(base[idx]);
  }
  return result;
}

function ScoreSelector({ value, onChange, nbNiveaux = 5, labels = [], colors = [] }) {
  const fallback = scaleColors(nbNiveaux);
  return (
    <MDBox display="flex" flexDirection="column" gap={0.4}>
      {Array.from({ length: nbNiveaux }, (_, i) => i + 1).map((n) => {
        const color = colors[n - 1] || fallback[n - 1];
        const label = labels[n - 1] || String(n);
        const selected = value === n;
        return (
          <MDBox
            key={n}
            onClick={() => onChange(selected ? null : n)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.7,
              borderRadius: "6px",
              border: `1.5px solid ${selected ? color : "#E0E6F0"}`,
              bgcolor: selected ? color + "18" : "transparent",
              cursor: "pointer",
              transition: "all 0.15s",
              "&:hover": { borderColor: color, bgcolor: color + "10" },
            }}
          >
            <MDBox
              sx={{
                width: 11,
                height: 11,
                borderRadius: "50%",
                bgcolor: color,
                flexShrink: 0,
                boxShadow: selected ? `0 0 0 3px ${color}33` : "none",
                transition: "box-shadow 0.15s",
              }}
            />
            <MDTypography
              variant="caption"
              sx={{
                fontWeight: selected ? 700 : 400,
                color: selected ? color : "text.secondary",
                fontSize: "0.8rem",
                flex: 1,
              }}
            >
              {label}
            </MDTypography>
            {selected && (
              <Icon sx={{ fontSize: "15px !important", color, flexShrink: 0 }}>check</Icon>
            )}
          </MDBox>
        );
      })}
    </MDBox>
  );
}

// ── Form defaults ─────────────────────────────────────────────────────────────

const BLANK_CARTO = {
  titre: "",
  description: "",
  periode: "",
  statut: "OUVERT",
  avec_facteurs: false,
};
const BLANK_RISQUE = {
  code: "",
  titre: "",
  famille_id: "",
  domaine_id: "",
  facteur_risque: "",
  causes: "",
  consequences: "",
  impact: null,
  probabilite: null,
  description_controles: "",
  qualite_controles: null,
  efficacite_operationnelle: null,
};
const BLANK_FAMILLE = { nom: "", description: "" };
const BLANK_NIVEAU = { nom: "", score_min: "", score_max: "", couleur: "#4CAF50", ordre: 0 };
const BLANK_FACTEUR = { nom: "", ordre: 0 };
const BLANK_PLAN = { action: "", responsable: "", echeance: "", statut: "EN_COURS" };

// ─────────────────────────────────────────────────────────────────────────────

function GestionRisques() {
  const { user } = useContext(AuthContext);
  const userRole = user?.profile?.role || user?.role;
  const canEdit = userRole !== "ADMIN_GLOBAL";
  const isAdminGlobal = !canEdit;

  const [tab, setTab] = useState(0);
  const [matriceMode, setMatriceMode] = useState("RI"); // "RI" | "RR" | "RR_POS"
  const [activeModule, setActiveModule] = useState(0); // 0 = Risques, 1 = Conformité
  const [selectedCarto, setSelectedCarto] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState("");

  const DEFAULT_CONFIG = {
    nb_niveaux_impact: 5,
    nb_niveaux_probabilite: 5,
    nb_niveaux_dmr: 5,
    labels_impact: ["Négligeable", "Mineur", "Modéré", "Majeur", "Catastrophique"],
    labels_probabilite: ["Rare", "Peu fréquent", "Modéré", "Fréquent", "Très fréquent"],
    labels_adequation: [
      "Inadéquat",
      "Peu adéquat",
      "Modérément adéquat",
      "Adéquat",
      "Très adéquat",
    ],
    labels_efficacite: [
      "Inefficace",
      "Peu efficace",
      "Modérément efficace",
      "Efficace",
      "Très efficace",
    ],
  };

  const [cartographies, setCartographies] = useState([]);
  const [risques, setRisques] = useState([]);
  const [familles, setFamilles] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [domaines, setDomaines] = useState([]);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configForm, setConfigForm] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const [cartoDialog, setCartoDialog] = useState({ open: false, editing: null });
  const [cartoForm, setCartoForm] = useState(BLANK_CARTO);

  const [risqueDialog, setRisqueDialog] = useState({ open: false, editing: null });
  const [risqueForm, setRisqueForm] = useState(BLANK_RISQUE);

  const [planDialog, setPlanDialog] = useState({ open: false, risque: null });
  const [planForm, setPlanForm] = useState(BLANK_PLAN);
  const [editingPlan, setEditingPlan] = useState(null);

  const [familleDialog, setFamilleDialog] = useState({ open: false, editing: null });
  const [familleForm, setFamilleForm] = useState(BLANK_FAMILLE);

  const [niveauDialog, setNiveauDialog] = useState({ open: false, editing: null });
  const [niveauForm, setNiveauForm] = useState(BLANK_NIVEAU);

  const [facteurs, setFacteurs] = useState([]);
  const [facteurDialog, setFacteurDialog] = useState({ open: false, editing: null });
  const [facteurForm, setFacteurForm] = useState(BLANK_FACTEUR);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: "", id: null });

  // ── Fetchers ──────────────────────────────────────────────────────────────

  const fetchCartographies = useCallback(async () => {
    try {
      const res = await api.get("/api/risques/cartographies");
      setCartographies(res.data);
    } catch {
      showSnack("Erreur chargement cartographies", "error");
    }
  }, []);

  const fetchRisques = useCallback(async (cartoId) => {
    if (!cartoId) return;
    try {
      const res = await api.get("/api/risques/", { params: { cartographie_id: cartoId } });
      setRisques(res.data);
    } catch {
      showSnack("Erreur chargement risques", "error");
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get("/api/risques/config");
      setConfig(res.data);
      setConfigForm(res.data);
    } catch {
      /* ignore - use defaults */
    }
  }, []);

  const saveConfig = async () => {
    try {
      await api.put("/api/risques/config", configForm);
      setConfig(configForm);
      showSnack("Configuration enregistrée");
    } catch {
      showSnack("Erreur lors de la sauvegarde", "error");
    }
  };

  const fetchFamilles = useCallback(async (instId) => {
    try {
      const url = instId
        ? `/api/risques/familles?institution_id=${instId}`
        : "/api/risques/familles";
      const res = await api.get(url);
      setFamilles(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchNiveaux = useCallback(async (instId) => {
    try {
      const url = instId
        ? `/api/risques/niveaux-criticite?institution_id=${instId}`
        : "/api/risques/niveaux-criticite";
      const res = await api.get(url);
      setNiveaux(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchFacteurs = useCallback(async (instId) => {
    try {
      const url = instId
        ? `/api/risques/facteurs?institution_id=${instId}`
        : "/api/risques/facteurs";
      const res = await api.get(url);
      setFacteurs(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchDomaines = useCallback(async () => {
    try {
      const res = await api.get("/api/domaines");
      setDomaines(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        fetchCartographies(),
        fetchFamilles(),
        fetchNiveaux(),
        fetchFacteurs(),
        fetchDomaines(),
        fetchConfig(),
      ]);
      setLoading(false);
    })();
    if (isAdminGlobal) {
      api
        .get("/api/institutions/")
        .then((r) => setInstitutions(r.data))
        .catch(() => {});
    }
  }, [
    fetchCartographies,
    fetchFamilles,
    fetchNiveaux,
    fetchFacteurs,
    fetchDomaines,
    fetchConfig,
    isAdminGlobal,
  ]);

  useEffect(() => {
    if (isAdminGlobal && selectedInstitution) {
      fetchNiveaux(selectedInstitution);
      fetchFamilles(selectedInstitution);
      fetchFacteurs(selectedInstitution);
    }
  }, [selectedInstitution, isAdminGlobal, fetchNiveaux, fetchFamilles, fetchFacteurs]);

  useEffect(() => {
    if (selectedCarto) fetchRisques(selectedCarto.id);
  }, [selectedCarto, fetchRisques]);

  // ── Cartographie CRUD ─────────────────────────────────────────────────────

  const openCartoCreate = () => {
    setCartoForm(BLANK_CARTO);
    setCartoDialog({ open: true, editing: null });
  };

  const openCartoEdit = (c) => {
    setCartoForm({
      titre: c.titre,
      description: c.description || "",
      periode: c.periode || "",
      statut: c.statut,
      avec_facteurs: c.avec_facteurs || false,
    });
    setCartoDialog({ open: true, editing: c });
  };

  const saveCarto = async () => {
    try {
      let updated = null;
      if (cartoDialog.editing) {
        const res = await api.put(
          `/api/risques/cartographies/${cartoDialog.editing.id}`,
          cartoForm
        );
        updated = res.data;
      } else {
        await api.post("/api/risques/cartographies", cartoForm);
      }
      setCartoDialog({ open: false, editing: null });
      await fetchCartographies();
      if (updated && selectedCarto?.id === updated.id) {
        setSelectedCarto(updated);
      }
      showSnack("Cartographie enregistrée");
    } catch {
      showSnack("Erreur lors de la sauvegarde", "error");
    }
  };

  const selectCarto = (c) => {
    setSelectedCarto(c);
    setTab(1);
  };

  // ── Risque CRUD ───────────────────────────────────────────────────────────

  const openRisqueCreate = () => {
    setRisqueForm(BLANK_RISQUE);
    setRisqueDialog({ open: true, editing: null });
  };

  const openRisqueEdit = (r) => {
    setRisqueForm({
      code: r.code || "",
      titre: r.titre,
      famille_id: r.famille_id || "",
      domaine_id: r.domaine_id || "",
      facteur_risque: r.facteur_risque || "",
      causes: r.causes || "",
      consequences: r.consequences || "",
      impact: r.impact,
      probabilite: r.probabilite,
      description_controles: r.description_controles || "",
      qualite_controles: r.qualite_controles,
      efficacite_operationnelle: r.efficacite_operationnelle,
    });
    setRisqueDialog({ open: true, editing: r });
  };

  const saveRisque = async () => {
    try {
      const payload = {
        ...risqueForm,
        famille_id: risqueForm.famille_id || null,
        domaine_id: risqueForm.domaine_id || null,
        facteur_risque: risqueForm.facteur_risque || null,
      };
      if (risqueDialog.editing) {
        await api.put(`/api/risques/${risqueDialog.editing.id}`, payload);
        setRisqueDialog({ open: false, editing: null });
        await fetchRisques(selectedCarto.id);
        showSnack("Risque mis à jour");
      } else {
        const res = await api.post("/api/risques/", {
          ...payload,
          cartographie_id: selectedCarto.id,
        });
        setRisqueDialog({ open: false, editing: null });
        await fetchRisques(selectedCarto.id);
        showSnack(`Risque ${res.data.code} créé avec succès`);
      }
    } catch {
      showSnack("Erreur lors de la sauvegarde", "error");
    }
  };

  // ── Plans d'action CRUD ───────────────────────────────────────────────────

  const openPlanDialog = (risque) => {
    setPlanDialog({ open: true, risque });
    setPlanForm(BLANK_PLAN);
    setEditingPlan(null);
  };

  const refreshRisquesAndDialog = async () => {
    if (!selectedCarto) return [];
    const res = await api.get("/api/risques/", {
      params: { cartographie_id: selectedCarto.id },
    });
    setRisques(res.data);
    return res.data;
  };

  const savePlan = async () => {
    if (!planForm.action.trim()) return;
    try {
      const payload = {
        ...planForm,
        echeance: planForm.echeance || null,
        risque_id: planDialog.risque.id,
      };
      if (editingPlan) {
        await api.put(`/api/risques/plans/${editingPlan.id}`, payload);
      } else {
        await api.post("/api/risques/plans", payload);
      }
      const refreshed = await refreshRisquesAndDialog();
      const updated = refreshed.find((r) => r.id === planDialog.risque.id);
      if (updated) setPlanDialog((prev) => ({ ...prev, risque: updated }));
      setPlanForm(BLANK_PLAN);
      setEditingPlan(null);
      showSnack("Plan d'action enregistré");
    } catch {
      showSnack("Erreur", "error");
    }
  };

  const deletePlan = async (planId) => {
    try {
      await api.delete(`/api/risques/plans/${planId}`);
      const refreshed = await refreshRisquesAndDialog();
      const updated = refreshed.find((r) => r.id === planDialog.risque.id);
      if (updated) setPlanDialog((prev) => ({ ...prev, risque: updated }));
      showSnack("Plan supprimé");
    } catch {
      showSnack("Erreur", "error");
    }
  };

  // ── Famille CRUD ──────────────────────────────────────────────────────────

  const openFamilleCreate = () => {
    setFamilleForm(BLANK_FAMILLE);
    setFamilleDialog({ open: true, editing: null });
  };

  const openFamilleEdit = (f) => {
    setFamilleForm({ nom: f.nom, description: f.description || "" });
    setFamilleDialog({ open: true, editing: f });
  };

  const saveFamille = async () => {
    try {
      if (familleDialog.editing) {
        await api.put(`/api/risques/familles/${familleDialog.editing.id}`, familleForm);
      } else {
        await api.post("/api/risques/familles", familleForm);
      }
      setFamilleDialog({ open: false, editing: null });
      await fetchFamilles();
      showSnack("Famille enregistrée");
    } catch {
      showSnack("Erreur", "error");
    }
  };

  // ── Niveau CRUD ───────────────────────────────────────────────────────────

  const openNiveauCreate = () => {
    setNiveauForm(BLANK_NIVEAU);
    setNiveauDialog({ open: true, editing: null });
  };

  const openNiveauEdit = (n) => {
    setNiveauForm({
      nom: n.nom,
      score_min: n.score_min,
      score_max: n.score_max,
      couleur: n.couleur,
      ordre: n.ordre,
    });
    setNiveauDialog({ open: true, editing: n });
  };

  const saveNiveau = async () => {
    try {
      const payload = {
        ...niveauForm,
        score_min: Number(niveauForm.score_min),
        score_max: Number(niveauForm.score_max),
        ordre: Number(niveauForm.ordre),
      };
      if (niveauDialog.editing) {
        await api.put(`/api/risques/niveaux-criticite/${niveauDialog.editing.id}`, payload);
      } else {
        await api.post("/api/risques/niveaux-criticite", payload);
      }
      setNiveauDialog({ open: false, editing: null });
      await fetchNiveaux();
      showSnack("Niveau enregistré");
    } catch {
      showSnack("Erreur", "error");
    }
  };

  const initNiveauxDefaut = async (force = false) => {
    try {
      await api.post(`/api/risques/niveaux-criticite/init-defaut?force=${force}`);
      await fetchNiveaux();
      showSnack(
        force ? "Niveaux réinitialisés aux valeurs par défaut" : "Niveaux de criticité initialisés"
      );
    } catch (err) {
      showSnack(err?.response?.data?.detail || "Erreur", "error");
    }
  };

  // ── Facteur de risque (LBC/FT) CRUD ──────────────────────────────────────

  const openFacteurCreate = () => {
    setFacteurForm(BLANK_FACTEUR);
    setFacteurDialog({ open: true, editing: null });
  };

  const openFacteurEdit = (f) => {
    setFacteurForm({ nom: f.nom, ordre: f.ordre });
    setFacteurDialog({ open: true, editing: f });
  };

  const saveFacteur = async () => {
    try {
      const payload = { ...facteurForm, ordre: Number(facteurForm.ordre) };
      if (facteurDialog.editing) {
        await api.put(`/api/risques/facteurs/${facteurDialog.editing.id}`, payload);
      } else {
        await api.post("/api/risques/facteurs", payload);
      }
      setFacteurDialog({ open: false, editing: null });
      await fetchFacteurs();
      showSnack("Facteur de risque enregistré");
    } catch {
      showSnack("Erreur", "error");
    }
  };

  const initFacteursDefaut = async (force = false) => {
    try {
      await api.post(`/api/risques/facteurs/init-defaut?force=${force}`);
      await fetchFacteurs();
      showSnack(
        force ? "Facteurs réinitialisés aux valeurs par défaut" : "Facteurs de risque initialisés"
      );
    } catch (err) {
      showSnack(err?.response?.data?.detail || "Erreur", "error");
    }
  };

  // ── Delete confirm ────────────────────────────────────────────────────────

  const confirmDelete = (type, id) => setDeleteConfirm({ open: true, type, id });

  const executeDelete = async () => {
    const { type, id } = deleteConfirm;
    setDeleteConfirm({ open: false, type: "", id: null });
    try {
      if (type === "carto") {
        await api.delete(`/api/risques/cartographies/${id}`);
        if (selectedCarto?.id === id) {
          setSelectedCarto(null);
          setTab(0);
        }
        await fetchCartographies();
      } else if (type === "risque") {
        await api.delete(`/api/risques/${id}`);
        if (selectedCarto) await fetchRisques(selectedCarto.id);
      } else if (type === "famille") {
        await api.delete(`/api/risques/familles/${id}`);
        await fetchFamilles();
      } else if (type === "niveau") {
        await api.delete(`/api/risques/niveaux-criticite/${id}`);
        await fetchNiveaux();
      } else if (type === "facteur") {
        await api.delete(`/api/risques/facteurs/${id}`);
        await fetchFacteurs();
      }
      showSnack("Suppression effectuée");
    } catch {
      showSnack("Erreur lors de la suppression", "error");
    }
  };

  // ── Rapport ───────────────────────────────────────────────────────────────

  const generateRapport = async () => {
    try {
      const res = await api.get(`/api/risques/rapport/${selectedCarto.id}`);
      const win = window.open("", "_blank");
      if (!win) {
        showSnack("Autorisez les pop-ups pour générer le rapport", "warning");
        return;
      }
      win.document.write(buildRapportHtml(res.data, res.data.niveaux_criticite || niveaux, config));
      win.document.close();
      win.print();
    } catch {
      showSnack("Erreur génération rapport", "error");
    }
  };

  const exportRisquesCSV = () => {
    const cfg = rh.current.config || {};
    const niveaux = rh.current.niveaux || [];
    const lImpact = cfg.labels_impact || [];
    const lProba = cfg.labels_probabilite || [];
    const lQC = cfg.labels_adequation || [];
    const lEO = cfg.labels_efficacite || [];

    const scoreLabel = (score) => getLevel(score, niveaux)?.nom || "";

    const rows = risques.map((r) => ({
      code: r.code || `R${String(r.id).padStart(3, "0")}`,
      titre: r.titre,
      domaine_nom: r.domaine_nom || "",
      famille_nom: r.famille_nom || "",
      facteur_risque: r.facteur_risque || "",
      impact: r.impact ? lImpact[r.impact - 1] || "" : "",
      probabilite: r.probabilite ? lProba[r.probabilite - 1] || "" : "",
      criticite_inherente: scoreLabel(r.score_inherent),
      qualite_controles: r.qualite_controles ? lQC[r.qualite_controles - 1] || "" : "",
      efficacite_operationnelle: r.efficacite_operationnelle
        ? lEO[r.efficacite_operationnelle - 1] || ""
        : "",
      criticite_residuelle: scoreLabel(r.risque_residuel),
    }));

    const cols = [
      { key: "code", label: "Code" },
      { key: "titre", label: "Titre" },
      { key: "domaine_nom", label: "Domaine" },
      { key: "famille_nom", label: "Famille" },
      { key: "facteur_risque", label: "Facteur de risque" },
      { key: "impact", label: "Impact" },
      { key: "probabilite", label: "Probabilité" },
      { key: "criticite_inherente", label: "Criticité inhérente" },
      { key: "qualite_controles", label: "Qualité contrôles" },
      { key: "efficacite_operationnelle", label: "Efficacité opérationnelle" },
      { key: "criticite_residuelle", label: "Criticité résiduelle" },
    ];

    const name = selectedCarto?.titre
      ? `risques_${selectedCarto.titre.replace(/\s+/g, "_")}`
      : "risques";
    downloadCSV(name, rows, cols);
  };

  const generateSuivi = async () => {
    try {
      const res = await api.get(`/api/risques/rapport/${selectedCarto.id}`);
      const cartoMeta = {
        titre: selectedCarto.titre,
        institution: res.data.cartographie.institution,
        periode: selectedCarto.periode,
      };
      const win = window.open("", "_blank");
      if (!win) {
        showSnack("Autorisez les pop-ups pour générer le suivi", "warning");
        return;
      }
      win.document.write(
        buildSuiviHtml(risques, res.data.niveaux_criticite || niveaux, res.data.config, cartoMeta)
      );
      win.document.close();
    } catch {
      showSnack("Erreur génération suivi", "error");
    }
  };

  // ── Colonnes DataTable - Registre des risques ────────────────────────────

  const rh = useRef({});

  const risquesColumns = useMemo(
    () => [
      {
        Header: "Risque",
        accessor: "titre",
        width: "36%",
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <MDBox display="flex" flexDirection="column" gap={0.3}>
              <MDTypography color="info" fontWeight="bold" variant="caption">
                {r.code || `R${String(r.id).padStart(3, "0")}`}
              </MDTypography>
              <MDTypography
                color="dark"
                fontWeight="medium"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                }}
                variant="button"
              >
                {r.titre}
              </MDTypography>
              {(r.famille_nom || r.domaine_nom) && (
                <MDTypography color="text" variant="caption">
                  {[r.famille_nom, r.domaine_nom].filter(Boolean).join(" · ")}
                </MDTypography>
              )}
            </MDBox>
          );
        },
      },
      {
        Header: "Inhérent",
        accessor: "impact",
        width: "22%",
        Cell: ({ row }) => {
          const r = row.original;
          const cfg = rh.current.config;
          const impactLabel = r.impact ? (cfg.labels_impact || [])[r.impact - 1] || "-" : "-";
          const probaLabel = r.probabilite
            ? (cfg.labels_probabilite || [])[r.probabilite - 1] || "-"
            : "-";
          return (
            <MDBox alignItems="flex-start" display="flex" flexDirection="column" gap={0.3}>
              <MDTypography color="text" variant="caption">
                {impactLabel}
              </MDTypography>
              <MDTypography color="text" variant="caption">
                {probaLabel}
              </MDTypography>
              <ScoreChip niveaux={rh.current.niveaux} score={r.score_inherent} />
            </MDBox>
          );
        },
      },
      {
        Header: "Contrôles / Résiduel",
        accessor: "qualite_controles",
        width: "22%",
        Cell: ({ row }) => {
          const r = row.original;
          const cfg = rh.current.config;
          const qcLabel = r.qualite_controles
            ? (cfg.labels_adequation || [])[r.qualite_controles - 1] || "-"
            : "-";
          const eoLabel = r.efficacite_operationnelle
            ? (cfg.labels_efficacite || [])[r.efficacite_operationnelle - 1] || "-"
            : "-";
          return (
            <MDBox alignItems="flex-start" display="flex" flexDirection="column" gap={0.3}>
              <MDTypography color="text" variant="caption">
                {qcLabel}
              </MDTypography>
              <MDTypography color="text" variant="caption">
                {eoLabel}
              </MDTypography>
              <ScoreChip niveaux={rh.current.niveaux} score={r.risque_residuel} />
            </MDBox>
          );
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        width: "20%",
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <MDBox display="flex" gap={0.5}>
              <Tooltip title="Plans d'action">
                <IconButton
                  onClick={() => rh.current.openPlanDialog(r)}
                  size="small"
                  sx={{ color: "#7b1fa2" }}
                >
                  <Icon fontSize="small">assignment</Icon>
                </IconButton>
              </Tooltip>
              {rh.current.canEdit && (
                <>
                  <Tooltip title="Modifier">
                    <IconButton
                      onClick={() => rh.current.openRisqueEdit(r)}
                      size="small"
                      sx={{ color: "#1976d2" }}
                    >
                      <Icon fontSize="small">edit</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton
                      onClick={() => rh.current.confirmDelete("risque", r.id)}
                      size="small"
                      sx={{ color: "#d32f2f" }}
                    >
                      <Icon fontSize="small">delete</Icon>
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </MDBox>
          );
        },
      },
    ],
    []
  );

  // ── TAB 0 - Cartographies ─────────────────────────────────────────────────

  const renderCartographies = () => (
    <MDBox>
      <MDBox
        alignItems="center"
        display="flex"
        flexWrap="wrap"
        gap={1}
        justifyContent="space-between"
        mb={2}
      >
        <MDTypography fontWeight="bold" variant="h6">
          Cartographies des risques
        </MDTypography>
        {canEdit && (
          <MDButton
            color="info"
            onClick={openCartoCreate}
            size="small"
            variant="contained"
            sx={{ height: "45px" }}
          >
            <Icon sx={{ mr: 0.5 }}>add</Icon>Nouvelle cartographie
          </MDButton>
        )}
      </MDBox>
      {(isAdminGlobal && selectedInstitution
        ? cartographies.filter((c) => String(c.institution_id) === String(selectedInstitution))
        : cartographies
      ).length === 0 ? (
        <Card>
          <MDBox p={4} textAlign="center">
            <Icon sx={{ fontSize: 48, color: "grey.400" }}>map</Icon>
            <MDTypography color="text" mt={1} variant="h6">
              Aucune cartographie pour le moment
            </MDTypography>
          </MDBox>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {(isAdminGlobal && selectedInstitution
            ? cartographies.filter((c) => String(c.institution_id) === String(selectedInstitution))
            : cartographies
          ).map((c, i) => (
            <Grid item key={c.id} lg={4} md={6} xs={12}>
              <Card
                sx={{
                  animation: `${fadeInUp} 0.4s ease-out ${stagger(i, 0.07)} both`,
                  transition: "box-shadow 0.3s",
                  "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.1)" },
                }}
              >
                <MDBox p={2.5}>
                  <MDBox
                    alignItems="flex-start"
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <MDBox flex={1}>
                      <MDTypography
                        color="dark"
                        fontWeight="bold"
                        sx={{ lineHeight: 1.3 }}
                        variant="h6"
                      >
                        {c.titre}
                      </MDTypography>
                      {c.periode && (
                        <MDTypography color="text" variant="caption">
                          {c.periode}
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDBadge
                      badgeContent={c.statut}
                      color={c.statut === "OUVERT" ? "success" : "secondary"}
                      size="sm"
                      variant="gradient"
                    />
                  </MDBox>
                  {c.description && (
                    <MDTypography color="text" sx={{ display: "block", mb: 1 }} variant="caption">
                      {c.description}
                    </MDTypography>
                  )}
                  <MDBox alignItems="center" display="flex" gap={1} mb={2}>
                    <Icon sx={{ color: "#186A81", fontSize: 16 }}>security</Icon>
                    <MDTypography color="info" variant="caption">
                      {c.nb_risques} risque{c.nb_risques !== 1 ? "s" : ""}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" gap={1} justifyContent="flex-end">
                    <MDButton
                      color="info"
                      onClick={() => selectCarto(c)}
                      size="small"
                      variant="contained"
                    >
                      <Icon sx={{ mr: 0.5 }}>open_in_new</Icon>Ouvrir
                    </MDButton>
                    {canEdit && (
                      <>
                        <Tooltip title="Modifier">
                          <IconButton
                            onClick={() => openCartoEdit(c)}
                            size="small"
                            sx={{ color: "#1976d2" }}
                          >
                            <Icon>edit</Icon>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            onClick={() => confirmDelete("carto", c.id)}
                            size="small"
                            sx={{ color: "#d32f2f" }}
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </MDBox>
  );

  // ── TAB 1 - Risques ───────────────────────────────────────────────────────

  const renderRisques = () => {
    rh.current = { canEdit, niveaux, config, openPlanDialog, openRisqueEdit, confirmDelete };

    if (!selectedCarto)
      return (
        <MDBox p={4} textAlign="center">
          <MDTypography color="text" variant="h6">
            Sélectionnez une cartographie dans l&apos;onglet &quot;Cartographies&quot;
          </MDTypography>
        </MDBox>
      );
    return (
      <MDBox>
        <MDBox
          alignItems="center"
          display="flex"
          flexWrap="wrap"
          gap={1}
          justifyContent="space-between"
          mb={2}
        >
          <MDBox>
            <MDTypography fontWeight="bold" variant="h6">
              {selectedCarto.titre}
            </MDTypography>
            <MDTypography color="text" variant="caption">
              {risques.length} risque{risques.length !== 1 ? "s" : ""} enregistré
              {risques.length !== 1 ? "s" : ""}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1}>
            {canEdit && (
              <MDButton
                color="info"
                onClick={openRisqueCreate}
                size="small"
                variant="contained"
                sx={{ height: "45px" }}
              >
                <Icon sx={{ mr: 0.5 }}>add</Icon>Nouveau risque
              </MDButton>
            )}
            <MDButton
              color="info"
              onClick={generateRapport}
              size="small"
              variant="outlined"
              sx={{ height: "45px" }}
            >
              <Icon sx={{ mr: 0.5 }}>print</Icon>Rapport
            </MDButton>
            <MDButton
              color="warning"
              onClick={generateSuivi}
              size="small"
              variant="outlined"
              sx={{ height: "45px" }}
            >
              <Icon sx={{ mr: 0.5 }}>track_changes</Icon>Suivi
            </MDButton>
            <MDButton
              color="success"
              onClick={exportRisquesCSV}
              size="small"
              variant="outlined"
              sx={{ height: "45px" }}
              disabled={risques.length === 0}
            >
              <Icon sx={{ mr: 0.5 }}>download</Icon>Excel
            </MDButton>
            <MDButton
              color="info"
              onClick={() => setTab(0)}
              size="small"
              variant="text"
              sx={{ height: "45px" }}
            >
              <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>Retour
            </MDButton>
          </MDBox>
        </MDBox>

        <Card sx={{ mt: 2 }}>
          {risques.length === 0 ? (
            <MDBox p={4} textAlign="center">
              <Icon sx={{ color: "grey.400", fontSize: 40 }}>security</Icon>
              <MDTypography color="text" mt={1} variant="body2">
                Aucun risque enregistré dans cette cartographie.
              </MDTypography>
            </MDBox>
          ) : (
            <DataTable
              table={{ columns: risquesColumns, rows: risques }}
              isSorted={false}
              entriesPerPage
              showTotalEntries
            />
          )}
        </Card>
      </MDBox>
    );
  };

  // ── TAB 2 - Paramètres ────────────────────────────────────────────────────

  const renderParametres = () => (
    <Grid container spacing={3}>
      {/* ── Configuration de la matrice ── */}
      {canEdit && (
        <Grid item xs={12}>
          <Card>
            <MDBox p={2.5}>
              <MDTypography fontWeight="bold" mb={2} variant="h6">
                Configuration de la matrice
              </MDTypography>
              <Grid container spacing={3}>
                {/* Impact */}
                <Grid item md={6} xs={12}>
                  <MDTypography color="info" fontWeight="bold" mb={1} variant="button">
                    Axe Impact
                  </MDTypography>
                  <MDBox alignItems="center" display="flex" gap={2} mb={1.5}>
                    <MDTypography color="text" variant="caption">
                      Nombre de niveaux :
                    </MDTypography>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {[3, 4, 5, 6, 7].map((n) => (
                        <div
                          key={n}
                          onClick={() => setConfigForm((p) => ({ ...p, nb_niveaux_impact: n }))}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "bold",
                            userSelect: "none",
                            transition: "all 0.2s",
                            background:
                              configForm.nb_niveaux_impact === n
                                ? "linear-gradient(195deg, #0A3475, #186A81)"
                                : "#f0f2f5",
                            color: configForm.nb_niveaux_impact === n ? "#FFD740" : "#555",
                          }}
                        >
                          {n}
                        </div>
                      ))}
                    </div>
                  </MDBox>
                  <MDTypography color="text" sx={{ display: "block", mb: 0.5 }} variant="caption">
                    Labels (du moins grave au plus grave)
                  </MDTypography>
                  {Array.from({ length: configForm.nb_niveaux_impact }, (_, k) => k).map((k) => (
                    <TextField
                      key={k}
                      fullWidth
                      label={`Niveau ${k + 1}`}
                      onChange={(e) => {
                        const labels = [...(configForm.labels_impact || [])];
                        labels[k] = e.target.value;
                        setConfigForm((p) => ({ ...p, labels_impact: labels }));
                      }}
                      size="small"
                      sx={{ mb: 1 }}
                      value={(configForm.labels_impact || [])[k] || ""}
                    />
                  ))}
                </Grid>
                {/* Probabilité */}
                <Grid item md={6} xs={12}>
                  <MDTypography color="info" fontWeight="bold" mb={1} variant="button">
                    Axe Probabilité
                  </MDTypography>
                  <MDBox alignItems="center" display="flex" gap={2} mb={1.5}>
                    <MDTypography color="text" variant="caption">
                      Nombre de niveaux :
                    </MDTypography>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {[3, 4, 5, 6, 7].map((n) => (
                        <div
                          key={n}
                          onClick={() =>
                            setConfigForm((p) => ({ ...p, nb_niveaux_probabilite: n }))
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "bold",
                            userSelect: "none",
                            transition: "all 0.2s",
                            background:
                              configForm.nb_niveaux_probabilite === n
                                ? "linear-gradient(195deg, #0A3475, #186A81)"
                                : "#f0f2f5",
                            color: configForm.nb_niveaux_probabilite === n ? "#FFD740" : "#555",
                          }}
                        >
                          {n}
                        </div>
                      ))}
                    </div>
                  </MDBox>
                  <MDTypography color="text" sx={{ display: "block", mb: 0.5 }} variant="caption">
                    Labels (du moins probable au plus probable)
                  </MDTypography>
                  {Array.from({ length: configForm.nb_niveaux_probabilite }, (_, k) => k).map(
                    (k) => (
                      <TextField
                        key={k}
                        fullWidth
                        label={`Niveau ${k + 1}`}
                        onChange={(e) => {
                          const labels = [...(configForm.labels_probabilite || [])];
                          labels[k] = e.target.value;
                          setConfigForm((p) => ({ ...p, labels_probabilite: labels }));
                        }}
                        size="small"
                        sx={{ mb: 1 }}
                        value={(configForm.labels_probabilite || [])[k] || ""}
                      />
                    )
                  )}
                </Grid>

                {/* Contrôles QC / EO */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <MDTypography color="info" fontWeight="bold" mb={1} variant="button">
                    Dispositif de contrôle
                  </MDTypography>
                  <MDBox alignItems="center" display="flex" gap={2} mb={1.5}>
                    <MDTypography color="text" variant="caption">
                      Nombre de niveaux :
                    </MDTypography>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {[3, 4, 5, 6, 7].map((n) => (
                        <div
                          key={n}
                          onClick={() => setConfigForm((p) => ({ ...p, nb_niveaux_dmr: n }))}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            fontWeight: "bold",
                            userSelect: "none",
                            transition: "all 0.2s",
                            background:
                              configForm.nb_niveaux_dmr === n
                                ? "linear-gradient(195deg, #0A3475, #186A81)"
                                : "#f0f2f5",
                            color: configForm.nb_niveaux_dmr === n ? "#FFD740" : "#555",
                          }}
                        >
                          {n}
                        </div>
                      ))}
                    </div>
                  </MDBox>
                  <Grid container spacing={2}>
                    <Grid item md={6} xs={12}>
                      <MDTypography
                        color="text"
                        sx={{ display: "block", mb: 0.5 }}
                        variant="caption"
                      >
                        Labels Qualité des contrôles (QC)
                      </MDTypography>
                      {Array.from({ length: configForm.nb_niveaux_dmr }, (_, k) => k).map((k) => (
                        <TextField
                          key={k}
                          fullWidth
                          label={`Niveau ${k + 1}`}
                          onChange={(e) => {
                            const labels = [...(configForm.labels_adequation || [])];
                            labels[k] = e.target.value;
                            setConfigForm((p) => ({ ...p, labels_adequation: labels }));
                          }}
                          size="small"
                          sx={{ mb: 1 }}
                          value={(configForm.labels_adequation || [])[k] || ""}
                        />
                      ))}
                    </Grid>
                    <Grid item md={6} xs={12}>
                      <MDTypography
                        color="text"
                        sx={{ display: "block", mb: 0.5 }}
                        variant="caption"
                      >
                        Labels Efficacité opérationnelle (EO)
                      </MDTypography>
                      {Array.from({ length: configForm.nb_niveaux_dmr }, (_, k) => k).map((k) => (
                        <TextField
                          key={k}
                          fullWidth
                          label={`Niveau ${k + 1}`}
                          onChange={(e) => {
                            const labels = [...(configForm.labels_efficacite || [])];
                            labels[k] = e.target.value;
                            setConfigForm((p) => ({ ...p, labels_efficacite: labels }));
                          }}
                          size="small"
                          sx={{ mb: 1 }}
                          value={(configForm.labels_efficacite || [])[k] || ""}
                        />
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <MDBox display="flex" justifyContent="flex-end" mt={2}>
                <MDButton color="info" onClick={saveConfig} variant="contained">
                  <Icon sx={{ mr: 0.5 }}>save</Icon>Enregistrer la configuration
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      )}
      <Grid item md={6} xs={12}>
        <Card>
          <MDBox p={2.5}>
            <MDBox alignItems="center" display="flex" justifyContent="space-between" mb={2}>
              <MDTypography fontWeight="bold" variant="h6">
                Familles de risques
              </MDTypography>
              {canEdit && (
                <MDButton color="info" onClick={openFamilleCreate} size="small" variant="outlined">
                  <Icon sx={{ mr: 0.5 }}>add</Icon>Ajouter
                </MDButton>
              )}
            </MDBox>
            {familles.map((f) => (
              <MDBox
                key={f.id}
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                py={1}
                sx={{ borderBottom: "1px solid #f0f2f5" }}
              >
                <MDBox>
                  <MDTypography color="dark" fontWeight="medium" variant="button">
                    {f.nom}
                  </MDTypography>
                  {f.description && (
                    <MDTypography color="text" sx={{ display: "block" }} variant="caption">
                      {f.description}
                    </MDTypography>
                  )}
                </MDBox>
                {canEdit && (
                  <MDBox display="flex" gap={0.5}>
                    <Tooltip title="Modifier">
                      <IconButton
                        onClick={() => openFamilleEdit(f)}
                        size="small"
                        sx={{ color: "#1976d2" }}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        onClick={() => confirmDelete("famille", f.id)}
                        size="small"
                        sx={{ color: "#d32f2f" }}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                )}
              </MDBox>
            ))}
            {familles.length === 0 && (
              <MDTypography color="text" variant="caption">
                Aucune famille définie
              </MDTypography>
            )}
          </MDBox>
        </Card>
      </Grid>

      <Grid item md={6} xs={12}>
        <Card>
          <MDBox p={2.5}>
            <MDBox
              alignItems="center"
              display="flex"
              flexWrap="wrap"
              gap={1}
              justifyContent="space-between"
              mb={2}
            >
              <MDBox>
                <MDTypography fontWeight="bold" variant="h6">
                  Niveaux de criticité
                </MDTypography>
                <MDTypography color="text" variant="caption">
                  Définissez les plages de scores et leurs couleurs (matrice 5×5 : score = Impact ×
                  Probabilité, max 25)
                </MDTypography>
              </MDBox>
              {canEdit && (
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <Tooltip title="Réinitialiser aux 3 niveaux par défaut (FAIBLE / MODEREE / ELEVEE)">
                    <MDButton
                      color="warning"
                      onClick={() => initNiveauxDefaut(true)}
                      size="small"
                      variant="outlined"
                    >
                      <Icon sx={{ mr: 0.5 }}>restart_alt</Icon>
                      Réinitialiser
                    </MDButton>
                  </Tooltip>
                  <MDButton color="info" onClick={openNiveauCreate} size="small" variant="outlined">
                    <Icon sx={{ mr: 0.5 }}>add</Icon>Ajouter
                  </MDButton>
                </MDBox>
              )}
            </MDBox>

            {/* Aperçu des niveaux par défaut quand aucun n'est défini */}
            {niveaux.length === 0 && (
              <MDBox
                mb={2}
                p={1.5}
                sx={{ bgcolor: "#f8f9ff", borderRadius: 2, border: "1px dashed #b0bec5" }}
              >
                <MDTypography
                  color="text"
                  variant="caption"
                  fontWeight="bold"
                  sx={{ display: "block", mb: 1 }}
                >
                  Niveaux par défaut (cliquez sur &quot;Réinitialiser&quot; pour les créer) :
                </MDTypography>
                {[
                  { nom: "FAIBLE", score_min: 1, score_max: 4, couleur: "#4CAF50" },
                  { nom: "MODEREE", score_min: 5, score_max: 12, couleur: "#FFC107" },
                  { nom: "ELEVEE", score_min: 13, score_max: 25, couleur: "#F44336" },
                ].map((n) => (
                  <MDBox key={n.nom} display="flex" alignItems="center" gap={1.5} py={0.5}>
                    <MDBox
                      sx={{
                        bgcolor: n.couleur,
                        borderRadius: "6px",
                        height: 22,
                        width: 22,
                        flexShrink: 0,
                      }}
                    />
                    <MDTypography
                      color="dark"
                      fontWeight="medium"
                      variant="button"
                      sx={{ minWidth: 80 }}
                    >
                      {n.nom}
                    </MDTypography>
                    <MDTypography color="text" variant="caption">
                      Score {n.score_min} - {n.score_max}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            )}

            {niveaux.map((n) => (
              <MDBox
                key={n.id}
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                py={1}
                sx={{ borderBottom: "1px solid #f0f2f5" }}
              >
                <MDBox alignItems="center" display="flex" gap={1.5}>
                  <MDBox sx={{ bgcolor: n.couleur, borderRadius: "6px", height: 24, width: 24 }} />
                  <MDBox>
                    <MDTypography color="dark" fontWeight="medium" variant="button">
                      {n.nom}
                    </MDTypography>
                    <MDTypography color="text" sx={{ display: "block" }} variant="caption">
                      Score {n.score_min} - {n.score_max}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                {canEdit && (
                  <MDBox display="flex" gap={0.5}>
                    <Tooltip title="Modifier">
                      <IconButton
                        onClick={() => openNiveauEdit(n)}
                        size="small"
                        sx={{ color: "#1976d2" }}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        onClick={() => confirmDelete("niveau", n.id)}
                        size="small"
                        sx={{ color: "#d32f2f" }}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                )}
              </MDBox>
            ))}
          </MDBox>
        </Card>
      </Grid>

      <Grid item md={6} xs={12}>
        <Card>
          <MDBox p={2.5}>
            <MDBox
              alignItems="center"
              display="flex"
              flexWrap="wrap"
              gap={1}
              justifyContent="space-between"
              mb={2}
            >
              <MDBox>
                <MDTypography fontWeight="bold" variant="h6">
                  Facteurs de risque (LBC/FT)
                </MDTypography>
                <MDTypography color="text" variant="caption">
                  Liste utilisée pour classer les risques des cartographies « avec facteurs »
                </MDTypography>
              </MDBox>
              {canEdit && (
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <Tooltip title="Réinitialiser aux 7 facteurs par défaut">
                    <MDButton
                      color="warning"
                      onClick={() => initFacteursDefaut(true)}
                      size="small"
                      variant="outlined"
                    >
                      <Icon sx={{ mr: 0.5 }}>restart_alt</Icon>
                      Réinitialiser
                    </MDButton>
                  </Tooltip>
                  <MDButton
                    color="info"
                    onClick={openFacteurCreate}
                    size="small"
                    variant="outlined"
                  >
                    <Icon sx={{ mr: 0.5 }}>add</Icon>Ajouter
                  </MDButton>
                </MDBox>
              )}
            </MDBox>

            {facteurs.length === 0 && (
              <MDBox
                mb={2}
                p={1.5}
                sx={{ bgcolor: "#f8f9ff", borderRadius: 2, border: "1px dashed #b0bec5" }}
              >
                <MDTypography color="text" variant="caption">
                  Aucun facteur défini. Cliquez sur &quot;Réinitialiser&quot; pour créer les 7
                  facteurs par défaut (Clients, Produits et services, Zone géographique, Canal de
                  distribution, Organisation, SIG, Processus).
                </MDTypography>
              </MDBox>
            )}

            {facteurs.map((f) => (
              <MDBox
                key={f.id}
                alignItems="center"
                display="flex"
                justifyContent="space-between"
                py={1}
                sx={{ borderBottom: "1px solid #f0f2f5" }}
              >
                <MDTypography color="dark" fontWeight="medium" variant="button">
                  {f.nom}
                </MDTypography>
                {canEdit && (
                  <MDBox display="flex" gap={0.5}>
                    <Tooltip title="Modifier">
                      <IconButton
                        onClick={() => openFacteurEdit(f)}
                        size="small"
                        sx={{ color: "#1976d2" }}
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        onClick={() => confirmDelete("facteur", f.id)}
                        size="small"
                        sx={{ color: "#d32f2f" }}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </MDBox>
                )}
              </MDBox>
            ))}
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );

  // ── Main render ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox alignItems="center" display="flex" height="60vh" justifyContent="center">
          <CircularProgress color="info" />
        </MDBox>
      </DashboardLayout>
    );
  }

  const TABS = ["Cartographies", "Registre des risques", "Matrice de risques", "Paramètres"];

  // ── Matrice Impact × Probabilité (RI) ou Impact × Probabilité colorée par RR ─
  const renderMatrice = () => {
    if (!selectedCarto)
      return (
        <MDBox p={4} textAlign="center">
          <MDTypography color="text" variant="h6">
            Sélectionnez une cartographie dans l&apos;onglet &quot;Cartographies&quot;
          </MDTypography>
        </MDBox>
      );

    const nbImpact = config.nb_niveaux_impact || 5;
    const nbProba = config.nb_niveaux_probabilite || 5;
    const MATRICE_MODE_LABELS = { RI: "RI", RR: "RR", RR_POS: "RR + position" };

    function levelColor(score) {
      return getLevel(score, niveaux);
    }
    function cellColor(score) {
      const lvl = levelColor(score);
      return lvl ? lvl.couleur + "33" : "#f5f5f5";
    }
    function cellBorder(score) {
      const lvl = levelColor(score);
      return lvl ? lvl.couleur : "#e0e0e0";
    }
    // Position résiduelle (mode RR_POS). Point de départ : la réduction liée
    // aux contrôles (1 - NC/5) répartie à parts égales entre Impact et
    // Probabilité via sa racine carrée (option C retenue le 2026-10-18).
    // Un simple arrondi indépendant de chaque axe pouvait faire atterrir le
    // risque sur une case dont la couleur (Impact×Probabilité de la case)
    // ne correspondait pas à la vraie couleur de son score résiduel (point
    // rouge sur case jaune, etc. - retour du porteur de projet). On cherche
    // donc, parmi les cases dont le niveau correspond réellement au niveau
    // résiduel du risque (et qui ne dépassent pas ses valeurs inhérentes),
    // celle la plus proche de cette position idéale.
    function residualPosition(r) {
      const nc = r.niveau_controle;
      const facteur = nc != null ? Math.sqrt(Math.max(0, 1 - nc / 5)) : 1;
      const idealI = r.impact * facteur;
      const idealP = r.probabilite * facteur;
      const cibleNom = levelColor(r.risque_residuel)?.nom;
      const maxI = Math.min(nbImpact, r.impact);
      const maxP = Math.min(nbProba, r.probabilite);

      let meilleur = null;
      let meilleureDistance = Infinity;
      let meilleurConforme = null;
      let meilleureDistanceConforme = Infinity;
      for (let i = 1; i <= maxI; i++) {
        for (let p = 1; p <= maxP; p++) {
          const distance = (i - idealI) ** 2 + (p - idealP) ** 2;
          if (distance < meilleureDistance) {
            meilleureDistance = distance;
            meilleur = { impact: i, probabilite: p };
          }
          if (
            cibleNom &&
            levelColor(i * p)?.nom === cibleNom &&
            distance < meilleureDistanceConforme
          ) {
            meilleureDistanceConforme = distance;
            meilleurConforme = { impact: i, probabilite: p };
          }
        }
      }
      return meilleurConforme || meilleur || { impact: 1, probabilite: 1 };
    }
    const isResiduelMode = matriceMode === "RR" || matriceMode === "RR_POS";
    // Couleur du point d'un risque selon le mode
    function dotColor(r, riScore) {
      if (isResiduelMode) {
        const lvl = levelColor(r.risque_residuel);
        return lvl ? lvl.couleur : "#90a4ae";
      }
      return cellBorder(riScore);
    }
    function dotTooltip(r) {
      if (isResiduelMode) {
        const nc = r.niveau_controle;
        const residuel = r.risque_residuel;
        const lvl = levelColor(residuel);
        const ncStr = nc != null ? `NC=${nc.toFixed(1)}` : "NC=-";
        const rrStr = residuel != null ? `RR=${residuel.toFixed(2)}` : "RR=-";
        const posStr =
          matriceMode === "RR_POS"
            ? ` · position résiduelle I${residualPosition(r).impact}×P${
                residualPosition(r).probabilite
              }`
            : "";
        return `${r.titre} · RI=${r.impact * r.probabilite} · ${ncStr} · ${rrStr}${
          lvl ? ` (${lvl.nom})` : ""
        }${posStr}`;
      }
      return r.titre;
    }

    const cellRisques = {};
    risques.forEach((r) => {
      if (r.impact && r.probabilite) {
        const pos =
          matriceMode === "RR_POS"
            ? residualPosition(r)
            : { impact: r.impact, probabilite: r.probabilite };
        const key = `${pos.impact}-${pos.probabilite}`;
        if (!cellRisques[key]) cellRisques[key] = [];
        cellRisques[key].push(r);
      }
    });

    const CELL = 90;

    return (
      <Card sx={{ animation: `${fadeIn} 0.5s ease-out both` }}>
        <MDBox p={2.5}>
          <MDBox
            mb={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <MDBox>
              <MDTypography fontWeight="bold" variant="h6">
                Matrice de risques - {selectedCarto.titre}
              </MDTypography>
              <MDTypography color="text" variant="caption">
                {matriceMode === "RI" && "Position et couleur selon le risque inhérent"}
                {matriceMode === "RR" &&
                  "Position selon le risque inhérent - couleur des points selon le risque résiduel"}
                {matriceMode === "RR_POS" &&
                  "Position et couleur selon le risque résiduel (Impact et Probabilité réduits selon le niveau de contrôle)"}
              </MDTypography>
            </MDBox>
            {/* Toggle RI / RR */}
            <MDBox
              display="flex"
              alignItems="center"
              gap={0}
              sx={{
                border: "1.5px solid #D0D7E6",
                borderRadius: "8px",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {["RI", "RR", "RR_POS"].map((mode) => (
                <MDBox
                  key={mode}
                  onClick={() => setMatriceMode(mode)}
                  sx={{
                    px: 2.5,
                    py: 0.8,
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    letterSpacing: 0.5,
                    userSelect: "none",
                    transition: "all 0.18s",
                    background:
                      matriceMode === mode
                        ? "linear-gradient(195deg, #0A3475, #186A81)"
                        : "transparent",
                    color: matriceMode === mode ? "#FFD740" : "#6B7A99",
                    "&:hover": matriceMode !== mode ? { background: "#F2F5FB" } : {},
                  }}
                >
                  {MATRICE_MODE_LABELS[mode]}
                </MDBox>
              ))}
            </MDBox>
            {niveaux.length > 0 && (
              <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                {niveaux.map((n) => (
                  <MDBox
                    key={n.id}
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                      px: 1,
                      py: 0.4,
                      borderRadius: 1,
                      bgcolor: n.couleur + "22",
                      border: `1px solid ${n.couleur}`,
                    }}
                  >
                    <MDBox
                      sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: n.couleur }}
                    />
                    <MDTypography variant="caption" fontWeight="bold" sx={{ color: n.couleur }}>
                      {n.nom} ({n.score_min}-{n.score_max})
                    </MDTypography>
                  </MDBox>
                ))}
                {isResiduelMode && (
                  <MDBox
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      bgcolor: "#FFF8E1",
                      border: "1px solid #F9A825",
                      borderRadius: 1,
                    }}
                  >
                    <MDTypography variant="caption" sx={{ color: "#795548", fontSize: "0.7rem" }}>
                      {matriceMode === "RR_POS"
                        ? "⚡ Position et couleur selon le risque résiduel"
                        : "⚡ Points colorés par RR · bordure = zone RI"}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            )}
          </MDBox>

          <MDBox sx={{ overflowX: "auto" }}>
            {/* Wrapper : titre Y (Impact) à gauche + grille à droite */}
            <MDBox sx={{ display: "inline-flex", alignItems: "stretch" }}>
              {/* Titre axe Y - Impact (ordonnée) */}
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  flexShrink: 0,
                  mr: 0.5,
                }}
              >
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="text"
                  sx={{
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    fontSize: "0.7rem",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    opacity: 0.8,
                  }}
                >
                  ↑ Impact
                </MDTypography>
              </MDBox>

              <MDBox sx={{ display: "inline-block", minWidth: (nbProba + 1) * CELL + 52 }}>
                {/* Ligne header : probabilités (abscisse) */}
                <MDBox display="flex" alignItems="center">
                  <MDBox sx={{ width: 52, flexShrink: 0 }} />
                  {Array.from({ length: nbProba }, (_, j) => j + 1).map((p) => (
                    <MDBox
                      key={p}
                      sx={{
                        width: CELL,
                        textAlign: "center",
                        py: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <MDTypography variant="caption" fontWeight="bold" color="text">
                        {config.labels_probabilite?.[p - 1] ? (
                          <Tooltip title={config.labels_probabilite[p - 1]}>
                            <span>P{p}</span>
                          </Tooltip>
                        ) : (
                          `P${p}`
                        )}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>

                {/* Lignes : impact décroissant (haut = critique), ordonnée */}
                {Array.from({ length: nbImpact }, (_, k) => nbImpact - k).map((imp) => (
                  <MDBox key={imp} display="flex" alignItems="stretch">
                    {/* Label impact */}
                    <MDBox
                      sx={{
                        width: 52,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pr: 0.5,
                      }}
                    >
                      {config.labels_impact?.[imp - 1] ? (
                        <Tooltip title={config.labels_impact[imp - 1]}>
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color="text"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            I{imp}
                          </MDTypography>
                        </Tooltip>
                      ) : (
                        <MDTypography variant="caption" fontWeight="bold" color="text">
                          I{imp}
                        </MDTypography>
                      )}
                    </MDBox>
                    {/* Cellules */}
                    {Array.from({ length: nbProba }, (_, j) => j + 1).map((prob) => {
                      const score = imp * prob;
                      const key = `${imp}-${prob}`;
                      const cellR = cellRisques[key] || [];
                      return (
                        <Tooltip
                          key={prob}
                          title={
                            cellR.length > 0
                              ? cellR.map((r) => dotTooltip(r)).join(" · ")
                              : `RI=${score} - aucun risque`
                          }
                          arrow
                        >
                          <MDBox
                            sx={{
                              width: CELL,
                              height: CELL,
                              flexShrink: 0,
                              border: `1.5px solid ${cellBorder(score)}`,
                              bgcolor: cellColor(score),
                              borderRadius: "6px",
                              m: "2px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                              cursor: cellR.length > 0 ? "pointer" : "default",
                              transition: "transform 0.15s",
                              "&:hover":
                                cellR.length > 0
                                  ? {
                                      transform: "scale(1.06)",
                                      zIndex: 2,
                                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    }
                                  : {},
                            }}
                          >
                            <MDTypography
                              variant="caption"
                              sx={{ color: "#555", fontSize: "0.62rem", opacity: 0.7 }}
                            >
                              RI={score}
                            </MDTypography>
                            {cellR.length > 0 && (
                              <MDBox
                                display="flex"
                                flexWrap="wrap"
                                gap={0.3}
                                justifyContent="center"
                              >
                                {cellR.slice(0, 4).map((r) => {
                                  const dc = dotColor(r, score);
                                  const residuel = isResiduelMode ? r.risque_residuel : null;
                                  return (
                                    <MDBox
                                      key={r.id}
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        bgcolor: dc,
                                        border:
                                          matriceMode === "RR"
                                            ? `2px solid ${cellBorder(score)}`
                                            : "none",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        flexDirection: "column",
                                      }}
                                    >
                                      <MDTypography
                                        variant="caption"
                                        sx={{
                                          color: "white",
                                          fontSize: "0.55rem",
                                          fontWeight: 700,
                                          lineHeight: 1,
                                        }}
                                      >
                                        {r.code?.substring(0, 2) || r.id}
                                      </MDTypography>
                                      {isResiduelMode && residuel != null && (
                                        <MDTypography
                                          variant="caption"
                                          sx={{
                                            color: "rgba(255,255,255,0.85)",
                                            fontSize: "0.5rem",
                                            lineHeight: 1,
                                          }}
                                        >
                                          {residuel.toFixed(1)}
                                        </MDTypography>
                                      )}
                                    </MDBox>
                                  );
                                })}
                                {cellR.length > 4 && (
                                  <MDTypography
                                    variant="caption"
                                    sx={{ fontSize: "0.6rem", color: "#555" }}
                                  >
                                    +{cellR.length - 4}
                                  </MDTypography>
                                )}
                              </MDBox>
                            )}
                          </MDBox>
                        </Tooltip>
                      );
                    })}
                  </MDBox>
                ))}

                {/* Titre axe X - Probabilité (abscisse) */}
                <MDBox display="flex" alignItems="center" justifyContent="center" mt={0.5}>
                  <MDBox sx={{ width: 52, flexShrink: 0 }} />
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                    sx={{
                      fontSize: "0.7rem",
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      opacity: 0.8,
                    }}
                  >
                    Probabilité →
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
          </MDBox>

          {risques.filter((r) => !r.impact || !r.probabilite).length > 0 && (
            <MDBox mt={2} p={1.5} sx={{ bgcolor: "#f5f5f5", borderRadius: 2 }}>
              <MDTypography variant="caption" color="text">
                <strong>{risques.filter((r) => !r.impact || !r.probabilite).length}</strong>{" "}
                risque(s) non positionnés (impact ou probabilité non renseignés) :{" "}
                {risques
                  .filter((r) => !r.impact || !r.probabilite)
                  .map((r) => r.titre)
                  .join(", ")}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* ── Card principale ── */}
        <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
          {/* En-tête gradient */}
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="purple"
            borderRadius="lg"
            coloredShadow="dark"
            sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
          >
            <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
              {activeModule === 0 ? "Gestion des Risques" : "Conformité"}
            </MDTypography>
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
            >
              {activeModule === 0
                ? "Cartographie et analyse des risques de votre institution"
                : "Procédures internes · Référentiel · Cartographie · Plans d'action · Reporting"}
            </MDTypography>
          </MDBox>

          <MDBox px={2} pt={2} pb={2}>
            {/* Sélecteur de module */}
            <MDBox display="flex" flexWrap="wrap" mb={2} gap={1.5} alignItems="center">
              <MDBox
                display="flex"
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {[
                  { label: "Gestion des Risques", icon: "security" },
                  { label: "Conformité", icon: "fact_check" },
                ].map((m, i) => (
                  <div
                    key={i}
                    onClick={() => setActiveModule(i)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 16px",
                      cursor: "pointer",
                      background:
                        activeModule === i
                          ? "linear-gradient(195deg, #0A3475, #186A81)"
                          : "transparent",
                      color: activeModule === i ? "#FFD740" : "#555555",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      whiteSpace: "nowrap",
                      transition: "all 0.2s",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (activeModule !== i) e.currentTarget.style.backgroundColor = "#e8edf5";
                    }}
                    onMouseLeave={(e) => {
                      if (activeModule !== i) e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <span>{m.label}</span>
                  </div>
                ))}
              </MDBox>
            </MDBox>

            {/* Sélecteur d'institution (ADMIN_GLOBAL) */}
            {isAdminGlobal && (
              <MDBox
                mb={2}
                sx={{
                  background: "linear-gradient(135deg, #eef2f8 0%, #e8f0fb 100%)",
                  border: "1px solid #c8d8ee",
                  borderLeft: "4px solid #0A3475",
                  borderRadius: "10px",
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Icon sx={{ color: "#0A3475", fontSize: "1.3rem" }}>admin_panel_settings</Icon>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ color: "#0A3475", display: "block", lineHeight: 1.2 }}
                    >
                      Vue Globale
                    </MDTypography>
                    <MDTypography variant="caption" sx={{ color: "#7a8eab", fontSize: "0.68rem" }}>
                      Filtrez les données par institution
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox ml="auto">
                  <FormControl sx={{ minWidth: 240 }}>
                    <Select
                      displayEmpty
                      value={selectedInstitution || ""}
                      onChange={(e) => {
                        setSelectedInstitution(e.target.value);
                        setSelectedCarto(null);
                      }}
                      sx={{ height: "45px" }}
                      renderValue={(v) =>
                        v ? (
                          institutions.find((i) => i.id === Number(v))?.nom
                        ) : (
                          <span style={{ color: "#aaa", fontSize: "0.875rem" }}>
                            Sélectionner une institution
                          </span>
                        )
                      }
                    >
                      {institutions.map((inst) => (
                        <MenuItem key={inst.id} value={inst.id}>
                          <MDBox display="flex" alignItems="center" gap={0.75}>
                            <Icon sx={{ fontSize: "1rem", color: "#0A3475" }}>account_balance</Icon>
                            <span>{inst.nom}</span>
                          </MDBox>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </Card>

        {/* Tabs gestion des risques */}
        {activeModule === 0 && (!isAdminGlobal || selectedInstitution) && (
          <Card sx={{ mt: 3, animation: `${fadeInUp} 0.5s ease-out both` }}>
            <MDBox px={2} pt={2} pb={2}>
              <MDBox
                display="flex"
                flexWrap="wrap"
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: "#f8f9fa",
                  width: "fit-content",
                }}
              >
                {TABS.map((label, i) => {
                  const isDisabled = (i === 1 || i === 2) && !selectedCarto;
                  const isActive = tab === i;
                  return (
                    <div
                      key={i}
                      onClick={() => !isDisabled && setTab(i)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 16px",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        background: isActive
                          ? "linear-gradient(195deg, #0A3475, #186A81)"
                          : "transparent",
                        color: isActive ? "#FFD740" : isDisabled ? "#bbb" : "#555555",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        whiteSpace: "nowrap",
                        transition: "all 0.2s",
                        userSelect: "none",
                        opacity: isDisabled ? 0.6 : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive && !isDisabled)
                          e.currentTarget.style.backgroundColor = "#e8edf5";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive && !isDisabled)
                          e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {label}
                    </div>
                  );
                })}
              </MDBox>
            </MDBox>
          </Card>
        )}

        {/* Invite sélection institution */}
        {isAdminGlobal && !selectedInstitution && (
          <MDBox
            mt={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
              background: "#f8f9fa",
              borderRadius: "12px",
              border: "1px dashed #c8d8ee",
            }}
          >
            <Icon sx={{ fontSize: "3rem !important", color: "#b0c4de" }}>account_balance</Icon>
            <MDTypography variant="h6" sx={{ color: "#4B5E80", fontWeight: 600 }}>
              Sélectionnez une institution
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{ color: "#9e9e9e", textAlign: "center", maxWidth: 340 }}
            >
              Choisissez une institution dans le sélecteur ci-dessus pour afficher les données de
              gestion des risques et de conformité.
            </MDTypography>
          </MDBox>
        )}

        {/* Module Conformité */}
        {activeModule === 1 && (!isAdminGlobal || selectedInstitution) && (
          <MDBox mt={3}>
            <ConformiteContent skipFirstTabs={2} institutionId={selectedInstitution || undefined} />
          </MDBox>
        )}

        {/* Module Gestion des Risques - contenu */}
        {activeModule === 0 && (!isAdminGlobal || selectedInstitution) && (
          <MDBox mt={3}>
            {tab === 0 && renderCartographies()}
            {tab === 1 && renderRisques()}
            {tab === 2 && renderMatrice()}
            {tab === 3 && renderParametres()}
          </MDBox>
        )}
      </MDBox>

      {/* ── Cartographie dialog ── */}
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => setCartoDialog({ open: false, editing: null })}
        open={cartoDialog.open}
      >
        <DialogTitle>
          {cartoDialog.editing ? "Modifier la cartographie" : "Nouvelle cartographie"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Titre *"
              onChange={(e) => setCartoForm((p) => ({ ...p, titre: e.target.value }))}
              size="small"
              value={cartoForm.titre}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              onChange={(e) => setCartoForm((p) => ({ ...p, description: e.target.value }))}
              rows={2}
              size="small"
              value={cartoForm.description}
            />
            <TextField
              fullWidth
              label="Période (ex: 2024-S1)"
              onChange={(e) => setCartoForm((p) => ({ ...p, periode: e.target.value }))}
              size="small"
              value={cartoForm.periode}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                label="Statut"
                onChange={(e) => setCartoForm((p) => ({ ...p, statut: e.target.value }))}
                value={cartoForm.statut}
              >
                <MenuItem value="OUVERT">Ouvert</MenuItem>
                <MenuItem value="CLOS">Clos</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!cartoForm.avec_facteurs}
                  onChange={(e) => setCartoForm((p) => ({ ...p, avec_facteurs: e.target.checked }))}
                />
              }
              label="Tenir compte des facteurs de risque (LBC/FT)"
            />
            {cartoDialog.editing && cartoForm.avec_facteurs && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                <strong>Mode LBC/FT actif</strong> - les risques de cette cartographie disposent
                d&apos;un champ <em>Facteur de risque</em> (Clients, Produits, Pays&hellip;).
              </Alert>
            )}
            {cartoDialog.editing &&
              !cartoForm.avec_facteurs &&
              cartoDialog.editing.avec_facteurs && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  Vous êtes sur le point de <strong>désactiver le mode LBC/FT</strong>. Les facteurs
                  de risque déjà renseignés sur les risques seront conservés mais ignorés.
                </Alert>
              )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => setCartoDialog({ open: false, editing: null })}
            variant="text"
          >
            Annuler
          </MDButton>
          <MDButton
            color="info"
            disabled={!cartoForm.titre.trim()}
            onClick={saveCarto}
            variant="contained"
          >
            Enregistrer
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Risque dialog ── */}
      <Dialog
        fullWidth
        maxWidth="md"
        onClose={() => setRisqueDialog({ open: false, editing: null })}
        open={risqueDialog.open}
      >
        <DialogTitle>{risqueDialog.editing ? "Modifier le risque" : "Nouveau risque"}</DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <MDTypography color="info" fontWeight="bold" variant="button">
              Informations générales
            </MDTypography>
            <Grid container spacing={2}>
              <Grid item sm={3} xs={12}>
                <TextField
                  fullWidth
                  label="Code"
                  size="small"
                  value={risqueDialog.editing ? risqueForm.code : "Auto"}
                  InputProps={{ readOnly: true }}
                  helperText={risqueDialog.editing ? "Non modifiable" : "Généré par le serveur"}
                  sx={{ "& input": { color: "#9e9e9e" } }}
                />
              </Grid>
              <Grid item sm={9} xs={12}>
                <TextField
                  fullWidth
                  label="Titre du risque *"
                  onChange={(e) => setRisqueForm((p) => ({ ...p, titre: e.target.value }))}
                  size="small"
                  value={risqueForm.titre}
                />
              </Grid>
              {!selectedCarto?.avec_facteurs && (
                <Grid item sm={6} xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Famille</InputLabel>
                    <Select
                      label="Famille"
                      onChange={(e) => setRisqueForm((p) => ({ ...p, famille_id: e.target.value }))}
                      value={risqueForm.famille_id}
                    >
                      <MenuItem value="">
                        <em>- Aucune -</em>
                      </MenuItem>
                      {familles.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                          {f.nom}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item sm={6} xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Domaine</InputLabel>
                  <Select
                    label="Domaine"
                    onChange={(e) => setRisqueForm((p) => ({ ...p, domaine_id: e.target.value }))}
                    value={risqueForm.domaine_id}
                  >
                    <MenuItem value="">
                      <em>- Aucun -</em>
                    </MenuItem>
                    {domaines.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.nom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {selectedCarto?.avec_facteurs && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Facteur de risque (LBC/FT)</InputLabel>
                    <Select
                      label="Facteur de risque (LBC/FT)"
                      onChange={(e) =>
                        setRisqueForm((p) => ({ ...p, facteur_risque: e.target.value }))
                      }
                      value={risqueForm.facteur_risque}
                    >
                      <MenuItem value="">
                        <em>- Aucun -</em>
                      </MenuItem>
                      {facteurs.map((f) => (
                        <MenuItem key={f.id} value={f.nom}>
                          {f.nom}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Causes"
                  multiline
                  onChange={(e) => setRisqueForm((p) => ({ ...p, causes: e.target.value }))}
                  rows={2}
                  size="small"
                  value={risqueForm.causes}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Conséquences"
                  multiline
                  onChange={(e) => setRisqueForm((p) => ({ ...p, consequences: e.target.value }))}
                  rows={2}
                  size="small"
                  value={risqueForm.consequences}
                />
              </Grid>
            </Grid>

            <Divider />

            <MDTypography color="info" fontWeight="bold" variant="button">
              Risque inhérent (Score = Impact × Probabilité)
            </MDTypography>
            <Grid alignItems="flex-start" container spacing={2}>
              <Grid item sm={6} xs={12}>
                <MDTypography color="text" sx={{ display: "block", mb: 0.5 }} variant="caption">
                  Impact (I)
                </MDTypography>
                <ScoreSelector
                  nbNiveaux={config.nb_niveaux_impact}
                  labels={config.labels_impact || []}
                  colors={niveauxScale(niveaux, config.nb_niveaux_impact)}
                  onChange={(v) => setRisqueForm((p) => ({ ...p, impact: v }))}
                  value={risqueForm.impact}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <MDTypography color="text" sx={{ display: "block", mb: 0.5 }} variant="caption">
                  Probabilité (P)
                </MDTypography>
                <ScoreSelector
                  nbNiveaux={config.nb_niveaux_probabilite}
                  labels={config.labels_probabilite || []}
                  colors={niveauxScale(niveaux, config.nb_niveaux_probabilite)}
                  onChange={(v) => setRisqueForm((p) => ({ ...p, probabilite: v }))}
                  value={risqueForm.probabilite}
                />
              </Grid>
              {risqueForm.impact && risqueForm.probabilite && (
                <Grid item xs={12}>
                  <MDBox alignItems="center" display="flex" gap={1.5}>
                    <MDTypography color="text" variant="caption">
                      Score inhérent :
                    </MDTypography>
                    <ScoreChip
                      label
                      niveaux={niveaux}
                      score={risqueForm.impact * risqueForm.probabilite}
                    />
                  </MDBox>
                </Grid>
              )}
            </Grid>

            <Divider />

            <MDTypography color="info" fontWeight="bold" variant="button">
              Dispositif de contrôle
            </MDTypography>
            <TextField
              fullWidth
              label="Description des contrôles"
              multiline
              onChange={(e) =>
                setRisqueForm((p) => ({ ...p, description_controles: e.target.value }))
              }
              rows={2}
              size="small"
              value={risqueForm.description_controles}
            />
            <Grid alignItems="flex-start" container spacing={2}>
              <Grid item sm={6} xs={12}>
                <MDTypography color="text" sx={{ display: "block", mb: 0.5 }} variant="caption">
                  Qualité des contrôles (QC)
                </MDTypography>
                <ScoreSelector
                  nbNiveaux={config.nb_niveaux_dmr}
                  labels={config.labels_adequation || []}
                  colors={niveauxScale(niveaux, config.nb_niveaux_dmr)}
                  onChange={(v) => setRisqueForm((p) => ({ ...p, qualite_controles: v }))}
                  value={risqueForm.qualite_controles}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <MDTypography color="text" sx={{ display: "block", mb: 0.5 }} variant="caption">
                  Efficacité opérationnelle (EO)
                </MDTypography>
                <ScoreSelector
                  nbNiveaux={config.nb_niveaux_dmr}
                  labels={config.labels_efficacite || []}
                  colors={niveauxScale(niveaux, config.nb_niveaux_dmr)}
                  onChange={(v) => setRisqueForm((p) => ({ ...p, efficacite_operationnelle: v }))}
                  value={risqueForm.efficacite_operationnelle}
                />
              </Grid>
              {risqueForm.impact &&
                risqueForm.probabilite &&
                risqueForm.qualite_controles &&
                risqueForm.efficacite_operationnelle && (
                  <Grid item xs={12}>
                    {(() => {
                      const { nc, residuel } = computeScores(
                        risqueForm.impact,
                        risqueForm.probabilite,
                        risqueForm.qualite_controles,
                        risqueForm.efficacite_operationnelle
                      );
                      return residuel !== null ? (
                        <MDBox alignItems="center" display="flex" gap={1.5}>
                          <MDTypography color="text" variant="caption">
                            Risque résiduel :
                          </MDTypography>
                          <ScoreChip niveaux={niveaux} score={residuel} />
                        </MDBox>
                      ) : null;
                    })()}
                  </Grid>
                )}
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => setRisqueDialog({ open: false, editing: null })}
            variant="text"
          >
            Annuler
          </MDButton>
          <MDButton
            color="info"
            disabled={!risqueForm.titre.trim()}
            onClick={saveRisque}
            variant="contained"
          >
            Enregistrer
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Plans d'action dialog ── */}
      <Dialog
        fullWidth
        maxWidth="md"
        scroll="paper"
        onClose={() => setPlanDialog({ open: false, risque: null })}
        open={planDialog.open}
      >
        <DialogTitle>Plans d&apos;action - {planDialog.risque?.titre}</DialogTitle>
        <DialogContent dividers sx={{ pt: "12px !important" }}>
          {(planDialog.risque?.causes || planDialog.risque?.consequences) && (
            <MDBox
              mb={2}
              p={1}
              sx={{ background: "#f0f4ff", borderRadius: 1, borderLeft: "3px solid #1A73E8" }}
            >
              {planDialog.risque?.causes && (
                <MDTypography color="text" sx={{ display: "block" }} variant="caption">
                  <strong>Causes du risque :</strong> {planDialog.risque.causes}
                </MDTypography>
              )}
              {planDialog.risque?.consequences && (
                <MDTypography color="text" sx={{ display: "block", mt: 0.3 }} variant="caption">
                  <strong>Conséquences :</strong> {planDialog.risque.consequences}
                </MDTypography>
              )}
            </MDBox>
          )}
          {canEdit && (
            <MDBox mb={2}>
              <MDTypography
                color="info"
                fontWeight="bold"
                sx={{ display: "block", mb: 1 }}
                variant="button"
              >
                {editingPlan ? "Modifier le plan" : "Ajouter un plan"}
              </MDTypography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Action *"
                    multiline
                    onChange={(e) => setPlanForm((p) => ({ ...p, action: e.target.value }))}
                    rows={2}
                    size="small"
                    value={planForm.action}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <TextField
                    fullWidth
                    label="Responsable"
                    onChange={(e) => setPlanForm((p) => ({ ...p, responsable: e.target.value }))}
                    size="small"
                    value={planForm.responsable}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    label="Échéance"
                    onChange={(e) => setPlanForm((p) => ({ ...p, echeance: e.target.value }))}
                    size="small"
                    type="date"
                    value={planForm.echeance}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Statut</InputLabel>
                    <Select
                      label="Statut"
                      onChange={(e) => setPlanForm((p) => ({ ...p, statut: e.target.value }))}
                      value={planForm.statut}
                    >
                      <MenuItem value="EN_COURS">En cours</MenuItem>
                      <MenuItem value="REALISE">Réalisé</MenuItem>
                      <MenuItem value="REPORTE">Reporté</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <MDBox display="flex" gap={1} mt={1.5}>
                <MDButton
                  color="info"
                  disabled={!planForm.action.trim()}
                  onClick={savePlan}
                  size="small"
                  variant="contained"
                >
                  {editingPlan ? "Mettre à jour" : "Ajouter"}
                </MDButton>
                {editingPlan && (
                  <MDButton
                    color="secondary"
                    onClick={() => {
                      setEditingPlan(null);
                      setPlanForm(BLANK_PLAN);
                    }}
                    size="small"
                    variant="text"
                  >
                    Annuler
                  </MDButton>
                )}
              </MDBox>
              <Divider sx={{ my: 2 }} />
            </MDBox>
          )}
          {(planDialog.risque?.plans_action || []).length === 0 ? (
            <MDTypography color="text" variant="caption">
              Aucun plan d&apos;action défini
            </MDTypography>
          ) : (
            (planDialog.risque?.plans_action || []).map((plan) => {
              const planEch = plan.echeance ? new Date(plan.echeance) : null;
              const nowPlan = new Date();
              const soonPlan = new Date();
              soonPlan.setDate(nowPlan.getDate() + 7);
              const isOverdue = planEch && plan.statut !== "REALISE" && planEch < nowPlan;
              const isSoon =
                planEch && plan.statut !== "REALISE" && !isOverdue && planEch < soonPlan;
              return (
                <MDBox
                  key={plan.id}
                  alignItems="flex-start"
                  display="flex"
                  justifyContent="space-between"
                  py={1.2}
                  sx={{
                    borderBottom: "1px solid #f0f2f5",
                    bgcolor: isOverdue ? "#fff3f3" : isSoon ? "#fffbf0" : "transparent",
                  }}
                >
                  <MDBox flex={1}>
                    <MDTypography
                      color="dark"
                      fontWeight="medium"
                      sx={{ display: "block" }}
                      variant="button"
                    >
                      {plan.action}
                    </MDTypography>
                    <MDBox display="flex" flexWrap="wrap" gap={2} mt={0.5}>
                      {plan.responsable && (
                        <MDTypography color="text" variant="caption">
                          👤 {plan.responsable}
                        </MDTypography>
                      )}
                      {planEch && (
                        <MDTypography
                          variant="caption"
                          sx={{
                            color: isOverdue ? "#c62828" : isSoon ? "#e65100" : "text.secondary",
                            fontWeight: isOverdue || isSoon ? 700 : 400,
                          }}
                        >
                          📅 {planEch.toLocaleDateString("fr-FR")}
                          {isOverdue && " ⚠️"}
                          {isSoon && " 🔔"}
                        </MDTypography>
                      )}
                      <Chip
                        color={
                          plan.statut === "REALISE"
                            ? "success"
                            : plan.statut === "REPORTE"
                            ? "warning"
                            : "info"
                        }
                        label={
                          plan.statut === "EN_COURS"
                            ? "En cours"
                            : plan.statut === "REALISE"
                            ? "Réalisé"
                            : "Reporté"
                        }
                        size="small"
                        sx={{ fontSize: "0.65rem", height: 20 }}
                      />
                    </MDBox>
                  </MDBox>
                  {canEdit && (
                    <MDBox display="flex" gap={0.5} ml={1}>
                      <Tooltip title="Modifier">
                        <IconButton
                          onClick={() => {
                            setEditingPlan(plan);
                            setPlanForm({
                              action: plan.action,
                              responsable: plan.responsable || "",
                              echeance: plan.echeance ? plan.echeance.slice(0, 10) : "",
                              statut: plan.statut,
                            });
                          }}
                          size="small"
                          sx={{ color: "#1976d2" }}
                        >
                          <Icon fontSize="small">edit</Icon>
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton
                          onClick={() => deletePlan(plan.id)}
                          size="small"
                          sx={{ color: "#d32f2f" }}
                        >
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      </Tooltip>
                    </MDBox>
                  )}
                </MDBox>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => setPlanDialog({ open: false, risque: null })}
            variant="text"
          >
            Fermer
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Famille dialog ── */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setFamilleDialog({ open: false, editing: null })}
        open={familleDialog.open}
      >
        <DialogTitle>
          {familleDialog.editing ? "Modifier la famille" : "Nouvelle famille de risque"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Nom *"
              onChange={(e) => setFamilleForm((p) => ({ ...p, nom: e.target.value }))}
              size="small"
              value={familleForm.nom}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              onChange={(e) => setFamilleForm((p) => ({ ...p, description: e.target.value }))}
              rows={2}
              size="small"
              value={familleForm.description}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => setFamilleDialog({ open: false, editing: null })}
            variant="text"
          >
            Annuler
          </MDButton>
          <MDButton
            color="info"
            disabled={!familleForm.nom.trim()}
            onClick={saveFamille}
            variant="contained"
          >
            Enregistrer
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Niveau dialog ── */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setNiveauDialog({ open: false, editing: null })}
        open={niveauDialog.open}
      >
        <DialogTitle>
          {niveauDialog.editing ? "Modifier le niveau" : "Nouveau niveau de criticité"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Nom *"
              onChange={(e) => setNiveauForm((p) => ({ ...p, nom: e.target.value }))}
              size="small"
              value={niveauForm.nom}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Score min"
                  onChange={(e) => setNiveauForm((p) => ({ ...p, score_min: e.target.value }))}
                  size="small"
                  type="number"
                  value={niveauForm.score_min}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Score max"
                  onChange={(e) => setNiveauForm((p) => ({ ...p, score_max: e.target.value }))}
                  size="small"
                  type="number"
                  value={niveauForm.score_max}
                />
              </Grid>
            </Grid>
            <MDBox>
              <MDTypography color="text" sx={{ display: "block", mb: 0.5 }} variant="caption">
                Couleur
              </MDTypography>
              <MDBox alignItems="center" display="flex" gap={1.5}>
                <input
                  onChange={(e) => setNiveauForm((p) => ({ ...p, couleur: e.target.value }))}
                  style={{
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                    height: 36,
                    width: 48,
                  }}
                  type="color"
                  value={niveauForm.couleur}
                />
                <MDTypography color="text" variant="caption">
                  {niveauForm.couleur}
                </MDTypography>
              </MDBox>
            </MDBox>
            <TextField
              fullWidth
              label="Ordre"
              onChange={(e) => setNiveauForm((p) => ({ ...p, ordre: e.target.value }))}
              size="small"
              type="number"
              value={niveauForm.ordre}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => setNiveauDialog({ open: false, editing: null })}
            variant="text"
          >
            Annuler
          </MDButton>
          <MDButton
            color="info"
            disabled={!niveauForm.nom.trim()}
            onClick={saveNiveau}
            variant="contained"
          >
            Enregistrer
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Facteur dialog ── */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setFacteurDialog({ open: false, editing: null })}
        open={facteurDialog.open}
      >
        <DialogTitle>
          {facteurDialog.editing ? "Modifier le facteur" : "Nouveau facteur de risque"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <MDBox display="flex" flexDirection="column" gap={2}>
            <TextField
              fullWidth
              label="Nom *"
              onChange={(e) => setFacteurForm((p) => ({ ...p, nom: e.target.value }))}
              size="small"
              value={facteurForm.nom}
            />
            <TextField
              fullWidth
              label="Ordre"
              onChange={(e) => setFacteurForm((p) => ({ ...p, ordre: e.target.value }))}
              size="small"
              type="number"
              value={facteurForm.ordre}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => setFacteurDialog({ open: false, editing: null })}
            variant="text"
          >
            Annuler
          </MDButton>
          <MDButton
            color="info"
            disabled={!facteurForm.nom.trim()}
            onClick={saveFacteur}
            variant="contained"
          >
            Enregistrer
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirm dialog ── */}
      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => setDeleteConfirm({ open: false, type: "", id: null })}
        open={deleteConfirm.open}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <MDTypography color="text" variant="body2">
            Cette action est irréversible. Souhaitez-vous continuer ?
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            onClick={() => setDeleteConfirm({ open: false, type: "", id: null })}
            variant="text"
          >
            Annuler
          </MDButton>
          <MDButton color="error" onClick={executeDelete} variant="contained">
            Supprimer
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        autoHideDuration={3500}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        open={snackbar.open}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

// ── Risk Matrix ───────────────────────────────────────────────────────────────

function RiskMatrix({ risques, niveaux, config }) {
  const nI = config?.nb_niveaux_impact || 5;
  const nP = config?.nb_niveaux_probabilite || 5;
  const labelsI = config?.labels_impact || [];
  const labelsP = config?.labels_probabilite || [];

  function cellColor(p, i) {
    const lvl = getLevel(p * i, niveaux);
    return lvl ? lvl.couleur + "33" : "#f5f5f5";
  }

  function cellBorder(p, i) {
    const lvl = getLevel(p * i, niveaux);
    return lvl ? `2px solid ${lvl.couleur}55` : "1px solid #eee";
  }

  function risquesInCell(p, i) {
    return risques.filter((r) => r.probabilite === p && r.impact === i);
  }

  // Rows: probability from nP down to 1
  const pRows = Array.from({ length: nP }, (_, k) => nP - k);
  // Cols: 0 = label column, then 1..nI
  const iCols = Array.from({ length: nI }, (_, k) => k + 1);

  return (
    <Card sx={{ animation: `${fadeIn} 0.5s ease-out both`, mb: 2 }}>
      <MDBox p={2.5}>
        <MDTypography fontWeight="bold" mb={2} variant="h6">
          Cartographie des risques ({nP}×{nI})
        </MDTypography>
        <MDBox sx={{ overflowX: "auto" }}>
          <MDBox
            sx={{
              display: "grid",
              gap: "2px",
              gridTemplateColumns: `90px repeat(${nI}, 1fr)`,
              gridTemplateRows: `repeat(${nP}, 60px) 40px`,
              minWidth: 380 + nI * 60,
            }}
          >
            {pRows.map((p) => (
              <>
                {/* Label column */}
                <MDBox
                  key={`lp-${p}`}
                  alignItems="center"
                  display="flex"
                  justifyContent="flex-end"
                  pr={1}
                >
                  <MDTypography
                    color="text"
                    sx={{ fontSize: "0.65rem", lineHeight: 1.2, textAlign: "right" }}
                    variant="caption"
                  >
                    {p}
                    {labelsP[p - 1] ? ` - ${labelsP[p - 1]}` : ""}
                  </MDTypography>
                </MDBox>
                {/* Data cells */}
                {iCols.map((i) => {
                  const items = risquesInCell(p, i);
                  return (
                    <MDBox
                      key={`c-${p}-${i}`}
                      sx={{
                        alignContent: "flex-start",
                        bgcolor: cellColor(p, i),
                        border: cellBorder(p, i),
                        borderRadius: "4px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.3,
                        overflow: "hidden",
                        p: 0.5,
                      }}
                    >
                      {items.map((r) => (
                        <Chip
                          key={r.id}
                          label={r.code || `R${String(r.id).padStart(2, "0")}`}
                          size="small"
                          sx={{
                            bgcolor: "white",
                            border: "1px solid #ccc",
                            fontSize: "0.6rem",
                            height: 18,
                          }}
                        />
                      ))}
                    </MDBox>
                  );
                })}
              </>
            ))}
            {/* Bottom row: impact labels */}
            <MDBox />
            {iCols.map((i) => (
              <MDBox key={i} alignItems="center" display="flex" justifyContent="center">
                <MDTypography
                  color="text"
                  sx={{ fontSize: "0.6rem", lineHeight: 1.2, textAlign: "center" }}
                  variant="caption"
                >
                  {i}
                  {labelsI[i - 1] ? ` - ${labelsI[i - 1]}` : ""}
                </MDTypography>
              </MDBox>
            ))}
          </MDBox>
          <MDBox display="flex" justifyContent="space-between" mt={0.5} px={1}>
            <MDTypography color="text" sx={{ fontSize: "0.7rem" }} variant="caption">
              ↑ Probabilité
            </MDTypography>
            <MDTypography color="text" sx={{ fontSize: "0.7rem" }} variant="caption">
              Impact →
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// ── HTML Suivi Plans d'action ─────────────────────────────────────────────────

function buildSuiviHtml(risques, niveaux, config, cartographie) {
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  const now = new Date();
  const dateFmt = now.toLocaleString("fr-FR");
  const lbl = (arr, idx) => (arr && idx != null && arr[idx - 1] ? arr[idx - 1] : null);

  const STATUT_LABEL = { EN_COURS: "En cours", REALISE: "Réalisé", REPORTE: "Reporté" };
  const STATUT_COLOR = { EN_COURS: "#1565c0", REALISE: "#2e7d32", REPORTE: "#e65100" };
  const STATUT_BG = { EN_COURS: "#e3f2fd", REALISE: "#e8f5e9", REPORTE: "#fff3e0" };

  const levelFor = (score) => {
    if (!score || !niveaux?.length) return null;
    return niveaux.find((n) => score >= n.score_min && score <= n.score_max) || null;
  };

  // Tous les plans avec leur risque parent
  const allPlans = [];
  risques.forEach((r) => {
    (r.plans_action || []).forEach((p) => allPlans.push({ ...p, risque: r }));
  });

  const total = allPlans.length;
  const enCours = allPlans.filter((p) => p.statut === "EN_COURS").length;
  const realises = allPlans.filter((p) => p.statut === "REALISE").length;
  const reportes = allPlans.filter((p) => p.statut === "REPORTE").length;
  const enRetard = allPlans.filter(
    (p) => p.echeance && p.statut !== "REALISE" && new Date(p.echeance) < now
  ).length;
  const tauxRealisation = total > 0 ? Math.round((realises / total) * 100) : 0;

  // Grouper par risque (seulement ceux avec plans)
  const risquesAvecPlans = risques.filter((r) => (r.plans_action || []).length > 0);

  const risqueRows = risquesAvecPlans
    .map((r) => {
      const lvRes = levelFor(r.risque_residuel);
      const lvInh = levelFor(r.score_inherent);
      const lv = lvRes || lvInh;
      const levelBg = lv ? `${lv.couleur}22` : "#f8f9fa";
      const rCode = r.code || `R${String(r.id).padStart(3, "0")}`;
      const impactLbl = lbl(config?.labels_impact, r.impact);
      const probaLbl = lbl(config?.labels_probabilite, r.probabilite);
      const riLabel = impactLbl && probaLbl ? `RI = ${impactLbl} × ${probaLbl}` : "";

      const planRows = (r.plans_action || [])
        .map((p, idx) => {
          const isLate = p.echeance && p.statut !== "REALISE" && new Date(p.echeance) < now;
          const echeanceFmt = p.echeance ? new Date(p.echeance).toLocaleDateString("fr-FR") : "-";
          const statutBg = STATUT_BG[p.statut] || "#f8f9fa";
          const statutColor = STATUT_COLOR[p.statut] || "#757575";
          const statutLabel = STATUT_LABEL[p.statut] || p.statut;
          return `<tr>
            <td style="color:#999;font-size:10px;text-align:center">${idx + 1}</td>
            <td style="font-size:11px;color:#444">${esc(p.cause || "-")}</td>
            <td style="font-size:11px">${esc(p.action)}</td>
            <td style="font-size:11px;text-align:center">${esc(p.responsable || "-")}</td>
            <td style="font-size:11px;text-align:center${
              isLate ? ";color:#c62828;font-weight:700" : ""
            }">
              ${esc(echeanceFmt)}${isLate ? " ⚠" : ""}
            </td>
            <td style="text-align:center">
              <span style="background:${statutBg};color:${statutColor};padding:2px 8px;
                border-radius:10px;font-size:10px;font-weight:700">
                ${esc(statutLabel)}
              </span>
            </td>
          </tr>`;
        })
        .join("");

      return `
        <tr style="background:${levelBg}">
          <td colspan="6" style="padding:8px 10px">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
              <span style="font-weight:700;color:#0A3475;font-size:12px">${esc(rCode)}</span>
              <span style="font-weight:600;font-size:12px;color:#222">${esc(r.titre)}</span>
              ${
                lv
                  ? `<span style="background:${lv.couleur};color:#fff;padding:1px 8px;
                border-radius:8px;font-size:10px;font-weight:700">${esc(lv.nom)}</span>`
                  : ""
              }
              ${riLabel ? `<span style="color:#666;font-size:10px">${esc(riLabel)}</span>` : ""}
              <span style="color:#666;font-size:11px;margin-left:auto">
                ${(r.plans_action || []).length} plan${(r.plans_action || []).length > 1 ? "s" : ""}
              </span>
            </div>
          </td>
        </tr>
        ${planRows}
        <tr><td colspan="6" style="padding:2px"></td></tr>`;
    })
    .join("");

  const tauxColor =
    tauxRealisation >= 80 ? "#2e7d32" : tauxRealisation >= 50 ? "#e65100" : "#c62828";
  const realisePl = realises > 1 ? "s" : "";
  const nbRisquesS = risquesAvecPlans.length;
  const nbRisquesPlS = nbRisquesS > 1 ? "s" : "";
  const cartTitre = esc(cartographie?.titre || "");
  const cartInst = esc(cartographie?.institution || "");
  const cartPeriode = esc(cartographie?.periode || "-");
  const cartPeriodeStr = cartPeriode !== "-" ? ` · Période : ${cartPeriode}` : "";
  const badgeTextColor =
    tauxColor === "#2e7d32" ? "#a5d6a7" : tauxColor === "#e65100" ? "#ffcc80" : "#ef9a9a";
  const kpiRetardClass = enRetard > 0 ? " err" : "";

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Suivi plans d'action - ${cartTitre}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;background:#fff;line-height:1.4}
  .no-print{position:fixed;top:14px;right:18px;display:flex;gap:8px;align-items:center;
    z-index:999;background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .btn-p:hover{background:#186A81}
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:32px 40px 26px;margin-top:80px;position:relative;overflow:hidden}
  .hdr::after{content:"";position:absolute;right:-40px;top:-40px;width:200px;height:200px;
    border-radius:50%;background:rgba(255,255,255,.05);pointer-events:none}
  .hdr-eye{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:20px;font-weight:700;letter-spacing:.4px;line-height:1.2}
  .hdr-sub{margin-top:6px;font-size:12px;opacity:.8}
  .hdr-date{margin-top:3px;font-size:11px;opacity:.6}
  .hdr-badge{position:absolute;right:40px;top:50%;transform:translateY(-50%);
    text-align:center;background:rgba(255,255,255,.13);border-radius:10px;
    padding:12px 18px;border:1px solid rgba(255,255,255,.2)}
  .hdr-badge .big{font-size:28px;font-weight:700;line-height:1;color:${badgeTextColor}}
  .hdr-badge .small{font-size:10px;opacity:.75;margin-top:3px}
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8}
  .kpi{flex:1;padding:14px 16px;border-right:1px solid #f0f0f0;text-align:center}
  .kpi:last-child{border-right:none}
  .kpi .v{font-size:22px;font-weight:700;color:#0A3475}
  .kpi .l{font-size:10px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
  .kpi.ok .v{color:#2e7d32}
  .kpi.warn .v{color:#e65100}
  .kpi.err .v{color:#c62828}
  .kpi.blue .v{color:#1565c0}
  .kpi.teal .v{color:#186A81}
  .taux-box{background:#f0f4ff;border-bottom:1px solid #d0daf5;padding:14px 40px;
    display:flex;align-items:center;gap:0}
  .taux-label{flex:1;font-size:12px;color:#333}
  .taux-label strong{display:block;font-size:13px;color:#0A3475;margin-bottom:2px}
  .taux-bar-wrap{flex:2;margin:0 24px}
  .taux-bar{background:#e0e0e0;height:10px;border-radius:5px;overflow:hidden}
  .taux-fill{height:10px;border-radius:5px;background:${tauxColor}}
  .taux-pct{font-size:26px;font-weight:700;color:${tauxColor};min-width:58px;text-align:right}
  .content{padding:22px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:26px 0 14px;letter-spacing:.3px}
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{background:#1a237e;color:#fff;padding:7px 10px;text-align:left;
    font-size:10px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:6px 10px;border-bottom:1px solid #eef0f5;vertical-align:top}
  tr:hover td{background:#f8faff}
  .ftr{margin-top:36px;padding:14px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;font-size:10px;color:#bbb}
  .empty{color:#999;font-style:italic;padding:16px 0;text-align:center}
  @media print{
    .no-print{display:none}
    body{font-size:11px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,.taux-box,th,.hdr-badge{
      -webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
    h2{page-break-after:avoid}
  }
  @page{size:A4 landscape;margin:10mm 8mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>

<div class="hdr">
  <div class="hdr-eye">ConformIMF · Gestion des risques</div>
  <h1>Suivi des Plans d'Action</h1>
  <div class="hdr-sub">${cartTitre}${cartInst ? ` · ${cartInst}` : ""}${cartPeriodeStr}</div>
  <div class="hdr-date">Généré le ${esc(dateFmt)}</div>
  <div class="hdr-badge">
    <div class="big">${tauxRealisation}%</div>
    <div class="small">Taux de<br>réalisation</div>
  </div>
</div>

<div class="kpi-strip">
  <div class="kpi"><div class="v">${total}</div><div class="l">Total plans</div></div>
  <div class="kpi blue"><div class="v">${enCours}</div><div class="l">En cours</div></div>
  <div class="kpi ok"><div class="v">${realises}</div><div class="l">Réalisés</div></div>
  <div class="kpi warn"><div class="v">${reportes}</div><div class="l">Reportés</div></div>
  <div class="kpi${kpiRetardClass}"><div class="v">${enRetard}</div><div class="l">En retard</div></div>
  <div class="kpi teal"><div class="v">${nbRisquesS}</div><div class="l">Risque${nbRisquesPlS} concerné${nbRisquesPlS}</div></div>
</div>

<div class="taux-box">
  <div class="taux-label">
    <strong>Avancement global des plans</strong>
    ${realises} plan${realisePl} réalisé${realisePl} sur ${total}
  </div>
  <div class="taux-bar-wrap">
    <div class="taux-bar"><div class="taux-fill" style="width:${tauxRealisation}%"></div></div>
    <div style="display:flex;justify-content:space-between;font-size:10px;color:#aaa;margin-top:3px">
      <span>0%</span><span>50%</span><span>100%</span>
    </div>
  </div>
  <div class="taux-pct">${tauxRealisation}%</div>
</div>

<div class="content">
<h2>Détail par risque - ${nbRisquesS} risque${nbRisquesPlS} avec plans</h2>
${
  total === 0
    ? `<p class="empty">Aucun plan d'action enregistré pour cette cartographie.</p>`
    : `<table>
  <thead><tr>
    <th style="width:28px">#</th>
    <th style="width:22%">Cause</th>
    <th>Action corrective</th>
    <th style="width:110px;text-align:center">Responsable</th>
    <th style="width:90px;text-align:center">Échéance</th>
    <th style="width:90px;text-align:center">Statut</th>
  </tr></thead>
  <tbody>${risqueRows}</tbody>
</table>`
}
</div>

<div class="ftr">
  <span>Document confidentiel - ${cartInst || "ConformIMF"}</span>
  <strong>${esc(dateFmt)}</strong>
</div>
</body>
</html>`;
}

// ── HTML Report ───────────────────────────────────────────────────────────────

function buildRapportHtml(data, niveaux, config) {
  const { cartographie, risques } = data;
  const avecFacteurs = cartographie.avec_facteurs;
  const FALLBACK = { nom: "-", couleur: "#9e9e9e" };
  const levelFor = (score) => getLevel(score, niveaux) || FALLBACK;
  const lbl = (arr, idx) => (arr && idx != null && arr[idx - 1] ? arr[idx - 1] : "-");
  const esc = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const statsByFacteur = {};
  if (avecFacteurs) {
    risques.forEach((r) => {
      const f = r.facteur_risque || "Non classifié";
      if (!statsByFacteur[f]) statsByFacteur[f] = { total: 0, critiques: 0 };
      statsByFacteur[f].total++;
      const lv = levelFor(r.risque_residuel ?? r.score_inherent);
      const diacriticsRe = new RegExp("[̀-ͯ]", "g");
      const lvNameNorm = (lv.nom || "").normalize("NFD").replace(diacriticsRe, "").toLowerCase();
      if (lvNameNorm.includes("critique") || lvNameNorm.includes("eleve")) {
        statsByFacteur[f].critiques++;
      }
    });
  }

  const rows = risques
    .map((r, idx) => {
      const lvInh = levelFor(r.score_inherent);
      const lvRes = levelFor(r.risque_residuel);
      const impactLbl = lbl((config || {}).labels_impact, r.impact);
      const probaLbl = lbl((config || {}).labels_probabilite, r.probabilite);
      const qcLbl = lbl((config || {}).labels_adequation, r.qualite_controles);
      const eoLbl = lbl((config || {}).labels_efficacite, r.efficacite_operationnelle);
      const thirdCol = avecFacteurs
        ? `<td>${esc(r.facteur_risque || "-")}</td>`
        : `<td>${esc(r.famille_nom || "-")}</td>`;
      return `<tr>
        <td style="color:#888;font-size:10px;text-align:center">${idx + 1}</td>
        <td>${esc(r.code || `R${String(r.id).padStart(3, "0")}`)}</td>
        <td>${esc(r.titre)}</td>
        ${thirdCol}
        <td>${esc(r.domaine_nom || "-")}</td>
        <td>${esc(impactLbl)}</td><td>${esc(probaLbl)}</td>
        <td style="background:${lvInh.couleur}33;font-weight:bold;color:${lvInh.couleur}">${esc(
        lvInh.nom
      )}</td>
        <td>${esc(qcLbl)}</td><td>${esc(eoLbl)}</td>
        <td style="background:${lvRes.couleur}33;font-weight:bold;color:${lvRes.couleur}">${esc(
        lvRes.nom
      )}</td>
      </tr>`;
    })
    .join("");

  const legend = niveaux
    .map(
      (n) =>
        `<span style="display:inline-flex;align-items:center;gap:4px;margin-right:12px">` +
        `<span style="width:14px;height:14px;border-radius:50%;background:${n.couleur};display:inline-block"></span>` +
        `<strong style="color:${n.couleur}">${n.nom}</strong></span>`
    )
    .join("");

  const facteurStatsHtml =
    avecFacteurs && Object.keys(statsByFacteur).length > 0
      ? `<h2>Répartition par facteur de risque (LBC/FT)</h2>
       <table><thead><tr><th>Facteur</th><th>Nb risques</th><th>Critiques/Élevés</th></tr></thead><tbody>
       ${Object.entries(statsByFacteur)
         .sort(([, a], [, b]) => b.total - a.total)
         .map(
           ([f, s]) =>
             `<tr><td>${esc(f)}</td><td style="text-align:center">${s.total}</td>
          <td style="text-align:center;color:${
            s.critiques > 0 ? "#c62828" : "#2e7d32"
          };font-weight:600">${s.critiques}</td></tr>`
         )
         .join("")}
       </tbody></table>`
      : "";

  const nbRisques = risques.length;
  const nbRisquesPl = nbRisques > 1 ? "s" : "";
  const nbAvecPlans = risques.filter((r) => (r.plans_action || []).length > 0).length;
  const cartoTitre = esc(cartographie.titre);
  const cartoInst = esc(cartographie.institution || "");
  const cartoPeriode = esc(cartographie.periode || "-");
  const cartoPeriodeStr = cartoPeriode !== "-" ? ` · Période : ${cartoPeriode}` : "";
  const cartoStatut = esc(cartographie.statut || "");
  const nbFacteurs = Object.keys(statsByFacteur).length;
  const facteurKpiHtml = avecFacteurs
    ? `<div class="kpi teal"><div class="v">${nbFacteurs}</div><div class="l">Facteurs LBC/FT</div></div>`
    : "";
  const modeLabel = avecFacteurs
    ? `<span style="background:#186A8120;color:#186A81;border:1px solid #186A8140;padding:1px 8px;border-radius:8px;font-size:10px;font-weight:700">Mode LBC/FT</span>`
    : "";

  return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Registre des risques - ${cartoTitre}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;font-size:12px;color:#333;background:#fff;line-height:1.4}
  .no-print{position:fixed;top:14px;right:18px;display:flex;gap:8px;z-index:999;
    background:rgba(255,255,255,.95);padding:8px 10px;border-radius:8px;
    box-shadow:0 2px 12px rgba(0,0,0,.15)}
  .btn-p{background:#0A3475;color:#fff;border:none;padding:8px 18px;border-radius:6px;
    cursor:pointer;font-size:12px;font-weight:700}
  .btn-p:hover{background:#186A81}
  .hdr{background:linear-gradient(135deg,#0A3475 0%,#1565c0 55%,#186A81 100%);
    color:#fff;padding:32px 40px 26px;margin-top:80px;position:relative;overflow:hidden}
  .hdr::after{content:"";position:absolute;right:-40px;top:-40px;width:200px;height:200px;
    border-radius:50%;background:rgba(255,255,255,.05);pointer-events:none}
  .hdr-eye{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;opacity:.7;margin-bottom:8px}
  .hdr h1{font-size:20px;font-weight:700;letter-spacing:.4px;line-height:1.2}
  .hdr-sub{margin-top:6px;font-size:12px;opacity:.8}
  .hdr-date{margin-top:3px;font-size:11px;opacity:.6}
  .hdr-badge{position:absolute;right:40px;top:50%;transform:translateY(-50%);
    text-align:center;background:rgba(255,255,255,.13);border-radius:10px;
    padding:12px 18px;border:1px solid rgba(255,255,255,.2)}
  .hdr-badge .big{font-size:28px;font-weight:700;line-height:1;color:#a5d6a7}
  .hdr-badge .small{font-size:10px;opacity:.75;margin-top:3px}
  .kpi-strip{display:flex;background:#fff;border-bottom:1px solid #e8e8e8}
  .kpi{flex:1;padding:14px 16px;border-right:1px solid #f0f0f0;text-align:center}
  .kpi:last-child{border-right:none}
  .kpi .v{font-size:22px;font-weight:700;color:#0A3475}
  .kpi .l{font-size:10px;color:#888;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
  .kpi.teal .v{color:#186A81}
  .content{padding:22px 40px}
  h2{font-size:13px;font-weight:700;color:#fff;
    background:linear-gradient(90deg,#0A3475,#186A81);
    padding:7px 14px;border-radius:4px;margin:26px 0 14px;letter-spacing:.3px}
  .legend{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:8px}
  table{width:100%;border-collapse:collapse;font-size:11px}
  th{background:#1a237e;color:#fff;padding:7px 10px;text-align:left;
    font-size:10px;font-weight:700;letter-spacing:.4px;text-transform:uppercase}
  td{padding:6px 10px;border-bottom:1px solid #eef0f5;vertical-align:top}
  tr:hover td{background:#f8faff}
  .ftr{margin-top:36px;padding:14px 40px;border-top:2px solid #e0e0e0;
    display:flex;justify-content:space-between;font-size:10px;color:#bbb}
  @media print{
    .no-print{display:none}
    body{font-size:11px}
    .hdr{margin-top:0}
    .hdr,.kpi-strip,th,.hdr-badge{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    tr{page-break-inside:avoid}
    h2{page-break-after:avoid}
  }
  @page{size:A4 landscape;margin:10mm 8mm}
</style>
</head>
<body>
<div class="no-print">
  <button class="btn-p" onclick="window.print()">🖨 Imprimer / PDF</button>
</div>

<div class="hdr">
  <div class="hdr-eye">ConformIMF · Gestion des risques</div>
  <h1>Registre des Risques ${modeLabel}</h1>
  <div class="hdr-sub">${cartoTitre} · ${cartoInst}${cartoPeriodeStr} · ${cartoStatut}</div>
  <div class="hdr-date">Généré le ${new Date().toLocaleDateString("fr-FR")}</div>
  <div class="hdr-badge">
    <div class="big">${nbRisques}</div>
    <div class="small">risque${nbRisquesPl}<br>enregistré${nbRisquesPl}</div>
  </div>
</div>

<div class="kpi-strip">
  <div class="kpi"><div class="v">${nbRisques}</div><div class="l">Total risques</div></div>
  <div class="kpi teal"><div class="v">${nbAvecPlans}</div><div class="l">Avec plans d'action</div></div>
  ${facteurKpiHtml}
</div>

<div class="content">
  <h2>Niveaux de criticité</h2>
  <div class="legend">${legend}</div>

  ${facteurStatsHtml}

  <h2>Registre complet - ${nbRisques} risque${nbRisquesPl}</h2>
  <table><thead><tr>
    <th style="width:28px">#</th>
    <th style="width:60px">Code</th>
    <th>Titre du risque</th>
    <th style="width:120px">${avecFacteurs ? "Facteur de risque" : "Famille"}</th>
    <th style="width:100px">Domaine</th>
    <th style="width:60px;text-align:center">Impact</th>
    <th style="width:70px;text-align:center">Probabilité</th>
    <th style="width:90px;text-align:center">Risque inhérent</th>
    <th style="width:80px;text-align:center">Qualité ctrl.</th>
    <th style="width:80px;text-align:center">Efficacité opér.</th>
    <th style="width:90px;text-align:center">Risque résiduel</th>
  </tr></thead>
  <tbody>${rows}</tbody></table>
</div>

<div class="ftr">
  <span>Document confidentiel - ${cartoInst || "ConformIMF"}</span>
  <strong>${new Date().toLocaleDateString("fr-FR")}</strong>
</div>
</body>
</html>`;
}

export default GestionRisques;
